import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2, ReceiptText, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { ErrorDialog, SuccessDialog } from '@/shared/components/ui/status-dialog';
import { Label } from '@/shared/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { Textarea } from '@/shared/components/ui/textarea';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { FileUploader } from '@/shared/components/file-upload/FileUploader';
import { ApiError } from '@/shared/api/client';
import { uploadDocument } from '@/shared/api/documents';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { useSession, useCanActOnVenta } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { resumenOperacionesClient } from '@/shared/api/controllers/resumenOperaciones';
import { contactosClient } from '@/shared/api/controllers/contactos';
import { CONDICION_CLIENTE_EXTERIOR } from '@/features/contactos/constants';
import { listDetallesByOperacion, type DetalleOperacionesItem } from '@/shared/api/controllers/detalleOperaciones';
import { listActiveMediosDePago } from '@/shared/api/controllers/mediosDePago';
import { listMpCuotas, MP_CUOTAS_QUERY_KEY } from '@/shared/api/controllers/mpCuotas';
import { listEcheqs } from '@/shared/api/controllers/echeqs';
import { EcheqDespachoAviso } from './components/EcheqDespachoAviso';
import { cajaBase, esCajaN } from '@/shared/lib/cajasEfectivo';
import { listActivePagosByOperacion } from '@/shared/api/controllers/pagos';
import {
  listActiveComprobantesByOperacion,
  listComprobantesByOperacion,
  type ComprobantePagoItem,
} from '@/shared/api/controllers/comprobantePagos';
import {
  anularComprobante,
  solicitarAnulacionComprobante,
  cargarComprobante,
  editarPromesa,
  getFacturaPercentageAuthorization,
  getFacturacionPreviaEstados,
  getUsadoContext,
  registrarComprobante,
  requestFacturaPercentageAuthorization,
  saveMediosPago,
  solicitarFacturacionPrevia,
  type RegistrarComprobantePayload,
  type SaveMediosPagoPayload,
} from '@/shared/api/controllers/ventas';
import { cuentaParaTechoFacturacion } from './lib/calc';
import type { Moneda } from './components/MoneyCell';
import { InvoiceBreakdownCard } from './components/InvoiceBreakdownCard';
import {
  MediosDePagoTable,
  type MedioRow,
} from './components/MediosDePagoTable';
import { ComprobantePagosTable } from './components/ComprobantePagosTable';
import { ProcesandoOverlay } from './components/ProcesandoOverlay';
import {
  RegistrarPagoDialog,
  type StagedPago,
} from './components/RegistrarPagoDialog';
import {
  SubirComprobanteDialog,
  type SubirComprobanteFormValue,
} from './components/SubirComprobanteDialog';
import {
  EditarPagoDialog,
  type EditarPagoFormValue,
} from './components/EditarPagoDialog';
import {
  computeBreakdown,
  round2,
  type InvoiceBreakdown,
} from './lib/pagosCalc';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';

/**
 * Pagos — Stage 4 port of `Screen_Pagos.pa.yaml`, matching the current
 * PowerApps design:
 *
 *  ┌──────────────┬──────────────────────────┐
 *  │ Invoice      │ Medios de pago (table)   │
 *  │ breakdown    │                          │
 *  └──────────────┴──────────────────────────┘
 *  ┌──────────────────────────────────────────┐
 *  │ Carga de pagos (ComprobantePagos table)  │
 *  │   + Registrar pago / Registrar promesa   │
 *  └──────────────────────────────────────────┘
 *  ┌──────────────────────────────────────────┐
 *  │ Totals pills + [Guardar ✓]               │
 *  └──────────────────────────────────────────┘
 *
 * Status_RO transitions in this screen: ONLY `Aceptado → Pendiente de
 * Pago` (when the first pago/promesa is registered). All further
 * transitions happen in Aprobaciones when admin confirms a
 * `04.ComprobantePagos` row.
 */
export function DetalleVentaPagosPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { profile } = useSession();
  const isAdministracion = profile?.perfil === 'administracion';
  // Nova role gets preview-only access — they can land here from the
  // Administración Nova tabs (Facturación Nova / Cuentas por Cobrar
  // Nova) or from the eye icon on the Ventas list. Either way every
  // mutation surface (medios edits, registrar pago, anular comprobante,
  // factura% requests) must be suppressed.
  const isNova = profile?.perfil === 'nova';
  // When navigated from the Administración module (Cobros a Confirmar
  // / Cuentas por Cobrar / Facturación), force a strict view-only
  // mode regardless of the user's perfil. Admins shouldn't be able to
  // edit medios, register pagos/promesas, or anular comprobantes from
  // this entry point.
  const navState = location.state as
    | { from?: string; tab?: string; justAccepted?: boolean }
    | null;
  const fromAdmin = navState?.from === 'administracion';
  // Nova reaches this screen from /ventas via the eye icon — the back
  // button must return to /ventas (not the default /ventas/:id, which
  // Nova isn't allowed to enter).
  const fromVentasList = navState?.from === 'ventas';
  // True when this page was opened straight after Aceptar — the user
  // *must* save medios + % factura or we revert the venta to Cotizado
  // on exit. Held in state for re-render and in a ref so the
  // useBlocker callback reads fresh values without depending on the
  // closure to refresh — proceed() in react-router 6 can otherwise
  // leave navigation stuck if the closure is stale.
  const [requiresPostAcceptanceSave, setRequiresPostAcceptanceSaveState] =
    useState(Boolean(navState?.justAccepted));
  const requiresPostAcceptanceSaveRef = useRef(requiresPostAcceptanceSave);
  const setRequiresPostAcceptanceSave = (next: boolean) => {
    requiresPostAcceptanceSaveRef.current = next;
    setRequiresPostAcceptanceSaveState(next);
  };
  // QA #14: respect the originating tab on back so a venta opened from
  // Facturación returns to Facturación, not the default Cobros.
  const adminBackUrl =
    navState?.tab && navState.tab !== 'cobros'
      ? `/administracion?tab=${navState.tab}`
      : '/administracion';

  // --- data loads ---
  // Country in every queryKey so AR/MX caches stay isolated.
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  // A venta belongs to one country — if the user switches active country
  // while on this screen the lookup would fail. Bounce back to the
  // ventas list. (useBlocker still gets to challenge this nav if the
  // venta is in the half-saved Aceptado state.)
  const mountedCountryRef = useRef(countryCode);
  useEffect(() => {
    if (mountedCountryRef.current !== countryCode) {
      navigate('/ventas', { replace: true });
    }
  }, [countryCode, navigate]);
  // Suppress fetches once the active country has diverged from the
  // country we mounted under — the protective navigate runs in an
  // effect, so without this guard the queryKey change fires a fetch
  // against the new country's scope before we redirect, producing a
  // 404 ("sales_headers <id> not found") for an id that lives in the
  // previous country.
  const countryStable = mountedCountryRef.current === countryCode;
  const headerQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'header', id],
    queryFn: () => resumenOperacionesClient.getItem(id!),
    enabled: Boolean(id) && countryStable,
  });
  const detallesQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'lines', id],
    queryFn: () => listDetallesByOperacion(id!),
    enabled: Boolean(id) && countryStable,
  });
  const mediosQ = useQuery({
    queryKey: ['pagos', countryCode, 'medios-de-pago'],
    queryFn: listActiveMediosDePago,
    staleTime: 5 * 60_000,
  });
  const pagosQ = useQuery({
    queryKey: ['pagos', countryCode, 'pagos-activos', id],
    queryFn: () => listActivePagosByOperacion(id!),
    enabled: Boolean(id) && countryStable,
  });
  const comprobantesQ = useQuery({
    queryKey: ['pagos', countryCode, 'comprobantes', id],
    queryFn: () => listActiveComprobantesByOperacion(id!),
    enabled: Boolean(id) && countryStable,
  });
  // OJO: esta página tiene CINCO queries bajo ['pagos', …] con formas de key
  // distintas (unas llevan countryCode, otras no). Por eso todas las
  // mutaciones invalidan por PREFIJO `['pagos']`. Invalidar la key puntual
  // dejaba esta lista sin refrescar: tras anular un cobro, el techo de
  // facturación se seguía calculando con la fila vieja y daba $0 disponible.
  //
  // Lista COMPLETA, sólo para el techo de facturación.
  // `listActiveComprobantesByOperacion` descarta los 'Anulado' antes de que
  // `sumComprobantesActivos` los vea, así que una factura viva sobre un cobro
  // anulado no consumía techo y se podía facturar dos veces la misma plata.
  // La tabla y los sums de pagado/cobrado siguen sobre la lista activa: acá
  // sólo nos interesan las filas anuladas que TODAVÍA tienen factura emitida.
  // Estado de las solicitudes de previa (pendiente / aprobada / rechazada) para
  // poder etiquetar cada fila. AR-only: fuera de AR el backend devuelve [].
  const previaEstadosQ = useQuery({
    queryKey: ['pagos', countryCode, 'previa-estados', id],
    queryFn: () => getFacturacionPreviaEstados(id!),
    enabled: Boolean(id) && countryStable && countryCode === 'AR',
  });
  // E-cheqs de la venta (AR): estado en cada tarjeta de Carga de pagos.
  const echeqsQ = useQuery({
    // Bajo ['pagos', …]: registrar / anular ya invalidan esa clave.
    queryKey: ['pagos', countryCode, 'echeqs', id],
    queryFn: () => listEcheqs({ ventaId: id! }),
    enabled: Boolean(id) && countryCode === 'AR',
    staleTime: 15_000,
  });
  const echeqByComprobante = useMemo(
    () => new Map((echeqsQ.data ?? []).map((e) => [e.comprobanteId ?? '', e])),
    [echeqsQ.data],
  );
  const previaEstadoByComprobante = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of previaEstadosQ.data ?? []) m.set(e.comprobanteId, e.status);
    return m;
  }, [previaEstadosQ.data]);
  // El motivo del rechazo, por comprobante. Va aparte del mapa de estados para
  // no cambiarle la forma a `ComprobantePagosTable`, que sólo necesita el
  // status para pintar el tag.
  const previaMotivoRechazoByComprobante = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of previaEstadosQ.data ?? []) {
      if (e.status === 'Rechazado' && e.motivoRechazo) m.set(e.comprobanteId, e.motivoRechazo);
    }
    return m;
  }, [previaEstadosQ.data]);

  const comprobantesFullQ = useQuery({
    queryKey: ['pagos', 'comprobantes-full', id],
    queryFn: () => listComprobantesByOperacion(id!),
    enabled: Boolean(id) && countryStable,
  });

  // Contacto's condición IVA (AR: stored in CP_C). An "Exento" client is
  // IVA-exempt, so operators typically leave porcentaje_factura at 0 — but
  // their payments still go WITH factura. For them the disponible-a-facturar
  // cap is based on the FULL venta total instead of the (zero) totalFactura,
  // so "Lleva Factura" isn't blocked. Shares the query key with
  // DetalleVentaPage so the contacto is served from cache when present.
  const clienteId = headerQ.data?.fields.IDCliente_RO ?? null;
  const contactoQ = useQuery({
    queryKey: ['detalle-venta', countryCode, 'contacto', clienteId],
    queryFn: () => contactosClient.getItem(clienteId!),
    enabled: Boolean(clienteId) && countryStable,
    staleTime: 60_000,
  });
  // Substring match (not strict equality): the condición-IVA field is
  // free-text, so operators may save "Exento" (the dropdown value) or a
  // hand-typed "IVA Exento" / "EXENTO". No other condición contains the
  // substring "exento", so this can't false-positive.
  const isExento = (contactoQ.data?.fields.CP_C ?? '')
    .toLowerCase()
    .includes('exento');

  // AR facturación needs the receptor's condición IVA (CP_C) + CUIT (CUIT_C).
  // These are captured by the vendedor on the contacto — so warn here, at pago
  // upload, if they're missing. Facturación later BLOCKS the CAE on the same
  // gap, and whoever approves facturas can't fix it (only the vendor has this
  // data). AR-only (MX facturación has its own CFDI fiscal handling).
  const clienteFiscalMissing = useMemo(() => {
    if (countryCode !== 'AR' || !contactoQ.data) return null;
    const f = contactoQ.data.fields;
    const missing: string[] = [];
    if (!(f.CP_C ?? '').trim()) missing.push('Condición frente al IVA');
    if ((f.CP_C ?? '').trim() === CONDICION_CLIENTE_EXTERIOR) {
      // Cliente del Exterior (Factura E): no tiene CUIT; se factura con país + tipo de entidad + ID impositivo.
      if (!(f.PaisDestinoArca_C ?? '').trim()) missing.push('País de destino');
      if (!(f.TipoEntidadExterior_C ?? '').trim()) missing.push('Tipo de entidad');
      if (!(f.IdImpositivoExterior_C ?? '').trim()) missing.push('ID impositivo');
    } else if (!(f.CUIT_C ?? '').trim()) missing.push('CUIT / Documento');
    return missing.length ? missing : null;
  }, [countryCode, contactoQ.data]);

  // Medios that exist in the active catalog but should NOT be offered as
  // a customer-payment option on a venta:
  //   - Caja Diferencia: MX Nova margin bucket. Auto-credited/debited by
  //     the Cobrar Fornitalia confirm (PR 3.3.f); never selected manually
  //     as a cobro target. Always hidden.
  //   - BBVA Italmexy: Italmexy's own bank. Hidden ONLY on Nova-flow
  //     ventas (`FNova_RO === 'SI'`), where it's reserved for the inter-
  //     company Pagar/Cobrar step (PR 3.3.e). On non-Nova ventas in MX
  //     (cash / direct channels) it's a valid customer-payment medio
  //     and must remain selectable.
  //   - Nova banks (Nova Banorte / Nova Mercado Pago): the INVERSE rule.
  //     Hidden on NON-Nova ventas (`FNova_RO !== 'SI'`, i.e. sub-`Limite
  //     Nova`). A venta that doesn't enter the Nova flow must not collect
  //     into a Nova bank — the cobro would land in the regular Cobros a
  //     Confirmar with `id_nova` null and no Nova orchestration (Factura
  //     Interna / Anticipo / inter-co settlement never fire), which is the
  //     exact mis-route this guard prevents.
  //   - Herconstru is the exception (2026-09-02): it's a plain MX cash
  //     caja that happens to be collected by Nova when the venta crosses
  //     `Limite Nova`, and the client raised that limit to disable the Nova
  //     flow. It stays selectable on every MX venta. Off the Nova flow the
  //     cobro is a regular cobro (no FI, no Anticipo — `novaAutoOrchestration`
  //     bails on `f_nova=false`) and its 5% comisión is emitted by
  //     /cobros/confirmar instead of /cobros-nova/confirmar.
  // Surfacing any of these on the wrong venta would let Ventas accidentally
  // route a customer cobro to an internal accounting bucket or an
  // un-orchestrated Nova caja. (Tesorería + Configuración + Compras flows
  // are unaffected — they fetch their own catalog and need full visibility
  // for admin maintenance.)
  //
  // Defensive: if a legacy pago or comprobante is already pinned to a
  // hidden medio, the row stays visible so the user can see / clear it.
  // New cobros can't pick any of these.
  const isFNova = headerQ.data?.fields.FNova_RO === 'SI';
  const mediosForVentaPicker = useMemo(() => {
    if (!mediosQ.data) return [];
    // Backend `isNovaChannelMedio` also matches 'Herconstru'; here we only
    // gate the Nova BANKS — see the Herconstru exception above.
    const isNovaBank = (name: string) => name.startsWith('Nova ');
    const isHidden = (name: string) => {
      if (name === 'Caja Diferencia') return true; // always internal-only
      if (isFNova) return name === 'BBVA Italmexy'; // reserved for inter-co
      return isNovaBank(name); // non-Nova venta → no Nova banks
    };
    const inUse = new Set<string>();
    for (const p of pagosQ.data ?? []) {
      if (p.fields.MedioPago_P) inUse.add(p.fields.MedioPago_P);
    }
    for (const c of comprobantesQ.data ?? []) {
      if (c.fields.MedioPago_CP) inUse.add(c.fields.MedioPago_CP);
    }
    return mediosQ.data.filter((m) => {
      const name = m.fields.Medio_MDP?.trim() || '';
      // La gemela "… N" nunca se elige: se elige "Efectivo Pesos" y el
      // server la resuelve según Lleva Factura (lib/cajasEfectivo). Sin
      // excepción por uso — un cobro en "N" se muestra con su caja base.
      if (esCajaN(name)) return false;
      // Cajas internas de Tesorería (toggle "Sólo Tesorería" del ABM, p. ej.
      // los FCI): no son para cobrar ventas.
      if (m.fields.SoloTesoreria_MDP !== 'SI' && !isHidden(name)) return true;
      return inUse.has(name);
    });
  }, [mediosQ.data, pagosQ.data, comprobantesQ.data, isFNova]);

  // Live updates while two parties act on the same venta — admin
  // confirms a pago / facturates a comprobante / flips factura uploaded
  // → ventas user's open Pagos tab refreshes instantly.
  useRealtimeInvalidate(
    [
      'sales_payments',
      'sales_payment_receipts',
      'sales_headers',
      'factura_percentage_authorizations',
    ],
    [['pagos'], ['detalle-venta'], ['ventas'], ['factura-pct-auth']],
  );

  const header = headerQ.data;
  // Honor the stored currency (ARS / MXN / USD). The server's `mapCurrency`
  // has already normalized legacy 'Pesos Argentinos' / 'Dolares' / etc. to
  // the 3-letter code, so we just whitelist the three known values and
  // fall back to ARS for safety.
  const monedaRaw = header?.fields.Moneda_RO;
  const moneda: Moneda =
    monedaRaw === 'USD' ? 'USD' : monedaRaw === 'MXN' ? 'MXN' : 'ARS';
  // Home currency for the active country drives what "Local" medios are
  // denominated in. AR: 'ARS'; MX: 'MXN'. The previous hardcoded 'ARS'
  // tagged every MX Local medio as ARS, which always disagreed with the
  // MXN venta moneda → the auto-distribution treated every row as
  // cross-currency and left the monto fields blank.
  const homeCurrency = activeCountry.currency.primary;
  const status = header?.fields.Status_RO ?? null;
  // Marketing may manage pagos only on its OWN ventas; every other role keeps
  // its access (`canAct` == `!isReadOnly` for non-marketing). `!canAct` folds
  // into the read-only gate below.
  const canAct = useCanActOnVenta(header?.fields.Vendedor_RO ?? null);
  // Factura% authorization state (MX only — AR returns umbral=0 from
  // the GET, which makes the local gate a no-op). A Pendiente row
  // freezes the Pagos screen until admin resolves it. lastResolved
  // lets us prefill the porcentaje from porcentaje_aplicado so the
  // operator doesn't re-type after an approval.
  const factPctQ = useQuery({
    queryKey: ['factura-pct-auth', countryCode, id],
    queryFn: () => getFacturaPercentageAuthorization(id!),
    enabled: Boolean(id) && countryStable,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });
  const factPctPending = factPctQ.data?.pending ?? null;
  const factPctUmbral = factPctQ.data?.umbral ?? 0;
  const factPctEnabled = factPctUmbral > 0;
  // MX Nova "horno usado" — exposes costoContable + rates so we can
  // predict whether the porcentaje the operator is about to save will
  // tip the venta below the cost+commission+IVA threshold. AR + non-
  // Nova MX ventas come back with applies=false and the gate
  // short-circuits.
  const usadoCtxQ = useQuery({
    queryKey: ['usado-context', countryCode, id],
    queryFn: () => getUsadoContext(id!),
    enabled: Boolean(id) && countryStable,
    staleTime: 30_000,
  });
  const usadoCtx = usadoCtxQ.data ?? null;
  // Status values that mean the venta has moved past the editable
  // medios/pagos stage. Server-side `PAGOS_STATUS_ALLOWED` (in
  // `server/src/routes/ventas.ts`) is the source of truth — anything
  // outside that set 409's on save. We mirror the "after-pago"
  // half here so the FE disables interaction + shows a clear banner
  // rather than letting the operator change fields and bounce off a
  // generic "no se pudieron guardar los medios de pago" toast.
  // Surfaced 2026-06-01 on venta #425 (Cerrado since March) when
  // Stefania tried to drop porcentaje from 100% to 50%.
  const TERMINAL_STATUSES = new Set([
    'Pago Realizado',
    'Pendiente Despacho',
    'Despachado',
    'Pendiente Puesta Marcha',
    'Cerrado',
    'Anulado',
  ]);
  const terminalStatus = status && TERMINAL_STATUSES.has(status) ? status : null;
  const readOnly =
    fromAdmin ||
    isAdministracion ||
    isNova ||
    !canAct ||
    terminalStatus !== null ||
    !status ||
    factPctPending !== null;

  // --- breakdown state ---
  const initialPorcentaje = useMemo(() => {
    const stored = parseMoney(header?.fields.PorcentajeFactura_RO);
    // A stored 0 is a VALID, intentional value (e.g. IVA-exento clients
    // facturan 0%); only NULL/empty means "never set" → default to 100.
    // The old `stored > 0` guard silently reverted a saved 0 back to 100
    // on reload (and a re-save would then overwrite the real 0 in the DB).
    if (stored != null) return stored;
    // After admin resolves a factura% request, prefill the porcentaje
    // input with the applied value so the operator doesn't re-type.
    // Falls back to 100 when nothing's been negotiated.
    const lastResolved = factPctQ.data?.lastResolved;
    if (lastResolved?.porcentaje_aplicado != null) {
      return lastResolved.porcentaje_aplicado;
    }
    return 100;
  }, [header?.fields.PorcentajeFactura_RO, factPctQ.data?.lastResolved]);
  const [porcentaje, setPorcentaje] = useState<number>(100);
  // Track whether the user has manually edited the porcentaje since
  // mount. Without this, a late-arriving factPctQ (lastResolved) would
  // re-seed `porcentaje` from `initialPorcentaje` and clobber in-flight
  // typing. Mirrors the `dirtyMediosRef` pattern below.
  const porcentajeDirtyRef = useRef(false);
  // Snapshot of the porcentaje value at focus time. Used to revert the
  // input when the operator cancels the low-% approval dialog so they
  // see the value they started from rather than the typed-but-rejected
  // number.
  const porcentajeSnapshotRef = useRef<number>(initialPorcentaje);
  useEffect(() => {
    if (porcentajeDirtyRef.current) return;
    setPorcentaje(initialPorcentaje);
    porcentajeSnapshotRef.current = initialPorcentaje;
  }, [initialPorcentaje]);

  // Filas de medios: declaradas acá porque el recargo MP sale de ellas.
  const [medioRows, setMedioRows] = useState<MedioRow[]>([]);
  // Recargo MercadoPago por cuotas (ABM Cuotas MercadoPago, AR): % sobre el
  // total factura cuando la caja con cuotas está tildada con cuotas elegidas.
  const mpCuotasQ = useQuery({
    queryKey: MP_CUOTAS_QUERY_KEY,
    queryFn: listMpCuotas,
    enabled: countryCode === 'AR',
    staleTime: 60_000,
  });
  const opcionesCuotas = useMemo(
    () =>
      (mpCuotasQ.data ?? [])
        .filter((o) => o.status === 'Activo')
        .map((o) => ({ cuotas: o.cuotas, porcentaje: o.porcentaje }))
        .sort((a, b) => a.cuotas - b.cuotas),
    [mpCuotasQ.data],
  );
  const recargoActivo = recargoDeFilas(medioRows);
  const recargoPctActivo = recargoActivo?.pct ?? 0;

  const breakdown: InvoiceBreakdown = useMemo(
    () => breakdownConRecargo(detallesQ.data ?? [], porcentaje, medioRows),
    [detallesQ.data, porcentaje, medioRows],
  );

  // MX Nova "horno usado" live predicate. Returns true iff the gate
  // applies for this venta and the porcentaje the operator currently
  // has in the input would put `total_factura` below the minimum
  // (cost × (1+comisionNova) × (1+ivaMx)). Drives the inline warning
  // under the porcentaje input + the save-time popup gate.
  //
  // `totalFactura > 0` guard mirrors the server-side E1 fix: a venta
  // with porcentaje=0 has no factura side at all, so it's not "usado"
  // — without this guard the pill would falsely fire on every
  // all-cash MX Nova venta whose articles carry a costo_contable.
  const wouldBeUsado = useMemo(() => {
    if (!usadoCtx?.applies) return false;
    if (usadoCtx.facturaMinimo <= 0) return false;
    return (
      breakdown.totalFactura > 0 &&
      breakdown.totalFactura < usadoCtx.facturaMinimo
    );
  }, [usadoCtx, breakdown.totalFactura]);

  // --- medios de pago rows --- (state declared above, next to the breakdown)
  const [dirtyMedios, setDirtyMedios] = useState(false);
  // Ref mirror so the re-seed effect can see the latest `dirtyMedios`
  // without re-subscribing (the effect's identity must stay tied to the
  // query data, not to the dirty flag — otherwise setting dirty would
  // re-run the seed and wipe the user's in-progress edits).
  const dirtyMediosRef = useRef(false);
  useEffect(() => {
    dirtyMediosRef.current = dirtyMedios;
  }, [dirtyMedios]);

  // Seed rows whenever medios catalog + existing pagos both arrive. Guard
  // against clobbering local edits: React Query refetches on window focus
  // and on mutation invalidation, so the effect can fire while the user
  // is typing. If we already have dirty local state we keep it.
  useEffect(() => {
    if (!mediosQ.data) return;
    if (dirtyMediosRef.current) return;
    const existingByMedio = new Map(
      (pagosQ.data ?? []).map((p) => [p.fields.MedioPago_P ?? '', p]),
    );
    // Snapshot del recargo MP guardado en la venta: su % manda aunque el ABM
    // haya cambiado después.
    const snapCuotas = header?.fields.RecargoMpCuotas_RO ?? null;
    const snapPct = parseMoney(header?.fields.RecargoMpPct_RO);
    const rows: MedioRow[] = mediosForVentaPicker.map((m) => {
      const medioName = m.fields.Medio_MDP?.trim() || '';
      const existing = existingByMedio.get(medioName);
      const categoria: 'Local' | 'Internacional' =
        m.fields.Categoria_MDP === 'Internacional' ? 'Internacional' : 'Local';
      const rawTipo = m.fields.Tipo_MDP;
      const tipo: MedioRow['tipo'] =
        rawTipo === 'Efectivo' ||
        rawTipo === 'Billetera Virtual' ||
        rawTipo === 'Banco' ||
        rawTipo === 'E-cheq'
          ? rawTipo
          : null;
      const admiteCuotas = m.fields.Cuotas_MDP === 'SI';
      const cuotasPago = admiteCuotas ? Number(existing?.fields.Cuotas_P) || null : null;
      const recargoPct =
        cuotasPago == null
          ? null
          : cuotasPago === snapCuotas && snapPct != null
            ? snapPct
            : (opcionesCuotas.find((o) => o.cuotas === cuotasPago)?.porcentaje ?? null);
      return {
        medioPagoId: m.id,
        medio: medioName,
        categoria,
        tipo,
        pagoUnico: existing?.fields.PagoUnico_P === 'SI',
        distPagos: Boolean(existing && existing.fields.PagoUnico_P !== 'SI'),
        tipoCambio: existing?.fields.TipoCambio_P ?? '',
        monto: existing?.fields.Monto_P ?? '',
        pagoId: existing?.id,
        admiteCuotas,
        cuotas: cuotasPago,
        recargoPct,
      };
    });
    setMedioRows(rows);
    setDirtyMedios(false);
  }, [
    mediosForVentaPicker,
    pagosQ.data,
    header?.fields.RecargoMpCuotas_RO,
    header?.fields.RecargoMpPct_RO,
    opcionesCuotas,
  ]);

  // When the user changes % de Factura the venta total shifts, but the
  // monto already locked into the selected medios doesn't follow.
  // Re-run the same distribution rules used in `onRowPatch`:
  //   - Pago Único or single Dist. row → new total
  //   - 2 Dist. rows → factura/efectivo split when exactly one is
  //     Tipo='Efectivo' (efectivo row → montoCobroEfectivo, other →
  //     totalFactura); otherwise 50/50. Keeps the rebalance consistent
  //     with the onRowPatch path so changing % factura after picking
  //     the medios produces the same allocation the user would have
  //     gotten by picking them after the % factura.
  //   - 3+ Dist. rows → leave alone (admin distributed manually; we
  //     don't have a fair way to rebalance their custom split)
  // Cross-currency rows are skipped — TC is required first.
  //
  // Tracks the previous porcentaje so the initial mount (seeding from
  // the venta header) doesn't re-stamp values the seed effect already
  // populated.
  const prevPorcentajeRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevPorcentajeRef.current === null) {
      prevPorcentajeRef.current = porcentaje;
      return;
    }
    if (prevPorcentajeRef.current === porcentaje) return;
    prevPorcentajeRef.current = porcentaje;

    const isSameCurrency = (r: MedioRow) =>
      (r.categoria === 'Internacional' ? 'USD' : homeCurrency) === moneda;
    setMedioRows((prev) =>
      rellenarMontos(prev, breakdownConRecargo(detallesQ.data ?? [], porcentaje, prev, true), isSameCurrency),
    );
    // Effect is gated by the porcentaje-vs-prev compare above; deps just
    // need to track the trigger + read-through values. Listing the three
    // numeric breakdown fields (rather than `breakdown` directly) avoids
    // re-firing on every detalles refetch that returns identical totals.
  }, [
    porcentaje,
    breakdown.montoTotalCobro,
    breakdown.montoCobroEfectivo,
    breakdown.totalFactura,
    moneda,
    homeCurrency,
  ]);

  const onRowPatch = (medioPagoId: string, patch: Partial<MedioRow>) => {
    const lineas = detallesQ.data ?? [];
    const isSameCurrencyRow = (r: MedioRow) =>
      (r.categoria === 'Internacional' ? 'USD' : homeCurrency) === moneda;
    setMedioRows((prev) => {
      // Switching INTO Pago Único mode: one-method cobro. Every other
      // row is wiped — both checkboxes and their montos/tipos de cambio —
      // because the single-method flag implies "this method covers the
      // entire total". Keeps the distribution unambiguous.
      //
      // We also auto-populate the monto with the total factura so the
      // user doesn't retype it. Pago Único semantically means "this
      // method covers the entire pago" — so we always overwrite the
      // monto with the full total, even if it was previously set
      // (e.g. by a prior Dist. Pagos distribution that left a half).
      // The user can edit it down afterwards if needed; defaulting to
      // the full amount matches the legacy PowerApps behavior and the
      // QA expectation that switching to Pago Único produces a clean
      // single-instrument total.
      //
      // Cross-currency (medio Internacional on an ARS venta or vice
      // versa) is skipped: the user has to enter the tipo de cambio
      // before we know how many medio-currency units cover the total.
      if (patch.pagoUnico === true) {
        // Pago Único covers the *full* amount the customer pays —
        // factura + cash. Filling with totalFactura would leave the
        // un-invoiced portion uncovered. With the recargo MP of THIS row
        // (it becomes the only selected one).
        const target = prev.find((r) => r.medioPagoId === medioPagoId);
        const merged0 = target ? { ...target, ...patch, pagoUnico: true, distPagos: false } : null;
        const bk = breakdownConRecargo(lineas, porcentaje, merged0 ? [merged0] : [], true);
        const totalForFill = round2(bk.montoTotalCobro);
        return prev.map((r) => {
          if (r.medioPagoId !== medioPagoId) {
            return { ...r, pagoUnico: false, distPagos: false, monto: '', tipoCambio: '' };
          }
          const merged: MedioRow = { ...r, ...patch, pagoUnico: true, distPagos: false };
          const medioMoneda = merged.categoria === 'Internacional' ? 'USD' : homeCurrency;
          const sameCurrency = medioMoneda === moneda;
          if (sameCurrency && totalForFill > 0) {
            merged.monto = String(totalForFill);
          } else if (!sameCurrency) {
            // Cross-currency: clear the previous (potentially half)
            // monto so the user is prompted to type the right amount
            // after entering the TC.
            merged.monto = '';
          }
          return merged;
        });
      }
      // Switching INTO Dist. de Pagos: redistribute monto across all
      // dist rows based on the new count.
      //   1 dist row  → that row gets the full total
      //   2 dist rows → factura/efectivo split when exactly one is
      //                 Tipo='Efectivo' (cash medio → montoCobroEfectivo,
      //                 other medio → totalFactura); otherwise default
      //                 50/50. Example: Herconstru (Efectivo) + Nova
      //                 Banorte (Banco) on a 50%-factura venta → Herconstru
      //                 gets the cash half (no IVA), Nova Banorte gets the
      //                 factura half (with IVA).
      //   3+ dist rows → all blank (admin distributes manually)
      // Any prior Pago Único row is demoted (its monto cleared) — the
      // single-method mode is mutually exclusive with dist. Cross-
      // currency rows (Internacional medio on ARS venta or vice
      // versa) are left blank: the admin must enter the tipo de
      // cambio first to know how many units cover the venta total.
      if (patch.distPagos === true) {
        // First pass: apply the patch + clear the old Pago Único row.
        const intermediate = prev.map((r) => {
          if (r.medioPagoId === medioPagoId) {
            return { ...r, ...patch, distPagos: true, pagoUnico: false };
          }
          if (r.pagoUnico) {
            return { ...r, pagoUnico: false, monto: '', tipoCambio: '' };
          }
          return r;
        });
        // Second pass: compute per-row monto for every dist row, with the
        // recargo MP the new selection carries.
        const bk = breakdownConRecargo(lineas, porcentaje, intermediate, true);
        const totalForFill = round2(bk.montoTotalCobro);
        const distRows = intermediate.filter((r) => r.distPagos);
        const distCount = distRows.length;
        const isSameCurrency = (r: MedioRow) =>
          (r.categoria === 'Internacional' ? 'USD' : homeCurrency) === moneda;
        const montoForRow = montoForTwoDistRows(
          distRows,
          bk,
          totalForFill,
          isSameCurrency,
        );
        return intermediate.map((r) => {
          if (!r.distPagos) return r;
          // Cross-currency: TC required before we can know per-row monto.
          if (!isSameCurrency(r)) return { ...r, monto: '' };
          if (distCount === 1 && totalForFill > 0) {
            return { ...r, monto: String(totalForFill) };
          }
          if (distCount === 2 && totalForFill > 0) {
            return { ...r, monto: montoForRow.get(r.medioPagoId) ?? '' };
          }
          // distCount >= 3 or total <= 0 → blank.
          return { ...r, monto: '' };
        });
      }
      // Otherwise: normal patch. Unchecking either mode on a row also
      // wipes that row's monto + tipo de cambio (an unchecked row is
      // conceptually "not participating").
      const next = prev.map((r) => {
        if (r.medioPagoId !== medioPagoId) return r;
        const merged: MedioRow = { ...r, ...patch };
        if (patch.pagoUnico === false && r.pagoUnico) {
          merged.monto = '';
          merged.tipoCambio = '';
        }
        if (patch.distPagos === false && r.distPagos) {
          merged.monto = '';
          merged.tipoCambio = '';
        }
        return merged;
      });
      // Cambió el recargo MP (cuotas elegidas, se destildó MP u otra caja que
      // compartía la factura): el total se movió -> mismas reglas de relleno
      // que % factura. Tipear un monto no cambia el recargo de relleno.
      const bkDespues = breakdownConRecargo(lineas, porcentaje, next, true);
      if (breakdownConRecargo(lineas, porcentaje, prev, true).recargoMp !== bkDespues.recargoMp) {
        return rellenarMontos(next, bkDespues, isSameCurrencyRow);
      }
      return next;
    });
    setDirtyMedios(true);
  };

  // --- derived footer numbers ---
  const { pagadoTotal } = useMemo(
    () => computePagadoTotals(medioRows, moneda, homeCurrency),
    [medioRows, moneda, homeCurrency],
  );
  // Recargo MP guardado en la venta (snapshot) y la caja que lo cobra: lo
  // muestran el diálogo de pago y las tarjetas de los cobros.
  const recargoMpVenta = useMemo(() => {
    const cuotas = header?.fields.RecargoMpCuotas_RO ?? null;
    const monto = parseMoney(header?.fields.RecargoMpMonto_RO) ?? 0;
    if (!cuotas || monto <= 0) return null;
    const caja = mediosForVentaPicker.find((m) => m.fields.Cuotas_MDP === 'SI')?.fields.Medio_MDP?.trim();
    if (!caja) return null;
    return { caja, cuotas, pct: parseMoney(header?.fields.RecargoMpPct_RO) ?? 0, monto };
  }, [header?.fields.RecargoMpCuotas_RO, header?.fields.RecargoMpMonto_RO, header?.fields.RecargoMpPct_RO, mediosForVentaPicker]);
  const totalCobro = round2(breakdown.montoTotalCobro);
  // Pendiente / total on the medios-table footer measure cash coverage
  // against the *full* customer charge (factura + efectivo). Using
  // totalFactura would zero out the un-invoiced cash portion.
  const pendiente = round2(totalCobro - pagadoTotal);
  // Confirmed / Pending-Cobro comprobantes already registered, in venta
  // currency terms (Pesos_CP when the venta is ARS and the pago is USD).
  const pagadoPorComprobantes = useMemo(
    () => sumComprobantesActivos(comprobantesQ.data ?? [], moneda),
    [comprobantesQ.data, moneda],
  );
  // Actual collection for the Cobranza panel: exclude promesas (Pendiente
  // de Cobro) so a scheduled future payment shows as pendiente, not as
  // 100% cobrado. `pagadoPorComprobantes` (promesa-inclusive) still drives
  // the registrar/save gates below, which must not double-collect a promesa.
  const cobradoPorComprobantes = useMemo(
    () =>
      sumComprobantesActivos(comprobantesQ.data ?? [], moneda, {
        excludePromesas: true,
      }),
    [comprobantesQ.data, moneda],
  );
  // PowerApps `lbl_DisponibleFacturar_P`: only comprobantes with a factura
  // attached count against the disponible-to-invoice. A comprobante
  // registered without a factura is still pendiente de cobro from the
  // administration POV, so it doesn't reduce what's left to invoice.
  const comprobantesParaTecho = useMemo(
    () => (comprobantesFullQ.data ?? []).filter((c) => cuentaParaTechoFacturacion(c.fields)),
    [comprobantesFullQ.data],
  );

  const facturadoPorComprobantes = useMemo(
    () =>
      sumComprobantesActivos(comprobantesParaTecho, moneda, {
        requireFactura: true,
      }),
    [comprobantesParaTecho, moneda],
  );
  // Normally the invoiceable ceiling is the porcentaje-driven totalFactura.
  // For Exento clients (porcentaje_factura usually 0, no IVA) we cap at the
  // full customer charge instead, so payments can be marked Lleva Factura up
  // to the whole sale — bounded by the total so we never flag more than the
  // venta. The comprobante still routes through Facturación normally (that
  // queue keys off ContieneFactura_CP, not the venta's totalFactura).
  const techoFactura = isExento
    ? breakdown.montoTotalCobro
    : breakdown.totalFactura;
  const disponibleAFacturar = useMemo(
    () => Math.max(0, round2(techoFactura - facturadoPorComprobantes)),
    [techoFactura, facturadoPorComprobantes],
  );

  // Espejo del techo para lo que se cobra SIN factura. El %factura parte la
  // venta en `totalFactura` y `montoCobroEfectivo`; un cobro sin factura nunca
  // llega a Facturación, así que pasarse de `montoCobroEfectivo` deja la venta
  // facturada de menos — y el efectivo cae en la caja "… N". Al 100% vale 0:
  // todo cobro lleva factura. Las promesas reservan cupo, igual que en el techo.
  const cobradoSinFactura = useMemo(
    () => sumComprobantesActivos(comprobantesQ.data ?? [], moneda, { sinFactura: true }),
    [comprobantesQ.data, moneda],
  );
  const disponibleSinFactura = Math.max(
    0,
    round2(breakdown.montoCobroEfectivo - cobradoSinFactura),
  );
  // Para los diálogos que reemplazan una fila (Subir comprobante, Editar): el
  // cupo sin contarla. Sin clamp antes de devolverla: al 100% una promesa sin
  // factura vieja NO hereda cupo propio.
  const disponibleSinFacturaSin = (c: ComprobantePagoItem) =>
    Math.max(
      0,
      round2(
        breakdown.montoCobroEfectivo -
          cobradoSinFactura +
          sumComprobantesActivos([c], moneda, { sinFactura: true }),
      ),
    );

  // --- dialogs / status ---
  const [registrarKind, setRegistrarKind] = useState<'pago' | 'promesa' | null>(null);
  const [verComprobante, setVerComprobante] = useState<ComprobantePagoItem | null>(null);
  const [cargarComprobanteFor, setCargarComprobanteFor] =
    useState<ComprobantePagoItem | null>(null);
  const [editarPromesaFor, setEditarPromesaFor] =
    useState<ComprobantePagoItem | null>(null);
  // Pedir facturación previa dispara una aprobación y, si sale aprobada, una
  // emisión fiscal irreversible sobre plata que todavía no entró. El click
  // suelto en la fila era demasiado barato para eso: confirmación primero.
  const [confirmPrevia, setConfirmPrevia] = useState<ComprobantePagoItem | null>(null);
  const [confirmAnular, setConfirmAnular] = useState<ComprobantePagoItem | null>(
    null,
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Pending value held while the low-% confirmation dialog is open.
  // If the operator confirms, this becomes the new porcentaje AND
  // POSTs an authorization request. If they cancel, the porcentaje
  // input snaps back to the previous value.
  const [pendingLowPorcentaje, setPendingLowPorcentaje] = useState<number | null>(
    null,
  );
  // Free-text justification entered by the operator inside the
  // approval dialog. Rendered as `motivo` in the Aprobaciones queue
  // so the approver can see why the operator dropped below the
  // threshold.
  const [pendingLowMotivo, setPendingLowMotivo] = useState('');
  // Same predicate as `wouldBeUsado` but evaluated against the value
  // held in `pendingLowPorcentaje` — used to show the usado overlap
  // warning inside the factura% approval dialog. When the operator
  // picks a porc that's below BOTH the umbral AND the usado threshold,
  // the dialog flags both so the admin reviewing the request knows it
  // lands in usado territory.
  const pendingWouldBeUsado = useMemo(() => {
    if (pendingLowPorcentaje == null) return false;
    if (!usadoCtx?.applies) return false;
    if (usadoCtx.facturaMinimo <= 0) return false;
    const projected = computeBreakdown(
      detallesQ.data ?? [],
      pendingLowPorcentaje,
      recargoPctActivo,
    );
    return (
      projected.totalFactura > 0 &&
      projected.totalFactura < usadoCtx.facturaMinimo
    );
  }, [pendingLowPorcentaje, usadoCtx, detallesQ.data, recargoPctActivo]);

  // --- mutations ---
  const requestFactPctMutation = useMutation({
    mutationFn: async (args: { porcentajeSolicitado: number; motivo: string }) => {
      if (!id) throw new Error('no id');
      return requestFacturaPercentageAuthorization(id, {
        porcentajeSolicitado: args.porcentajeSolicitado,
        porcentajeAnterior: initialPorcentaje,
        motivo: args.motivo.trim() || undefined,
      });
    },
    onSuccess: (r) => {
      if ('ok' in r && r.ok) {
        toast.success(
          r.alreadyPending
            ? 'Ya existía una solicitud pendiente para esta venta.'
            : 'Solicitud de aprobación enviada.',
        );
        // Refresh the auth status so the banner + readOnly lock pick up.
        qc.invalidateQueries({ queryKey: ['factura-pct-auth'] });
        // Optimistic: add the venta SP id to every cached
        // factura-pct-pending list so the Pend.aprob badge appears on
        // Ventas / Mantenimiento the moment the operator navigates
        // back, without waiting for the next refetch round-trip. The
        // background invalidate still runs to reconcile.
        const spId = id ? Number(id) : null;
        if (spId != null && Number.isFinite(spId)) {
          qc.setQueriesData<number[]>(
            { queryKey: ['ventas', 'factura-pct-pending'] },
            (prev) => {
              if (!prev) return prev;
              if (prev.includes(spId)) return prev;
              return [...prev, spId];
            },
          );
        }
        qc.invalidateQueries({ queryKey: ['ventas', 'factura-pct-pending'] });
        // Bump the sidebar Aprobaciones counter immediately. Without
        // this, the new pending row only shows up after the next 30s
        // staleTime / a manual click into the page. Found 2026-05-19 —
        // QA saw the badge lag after submitting a factura% request.
        qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      } else if ('error' in r) {
        toast.error('No se pudo crear la solicitud', {
          description:
            'message' in r && r.message ? r.message : r.error,
        });
      }
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('[pagos] requestFacturaPercentageAuthorization failed', err);
      toast.error('No se pudo crear la solicitud', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });

  const handlePorcentajeFocus = () => {
    // Capture the value-before-edit so the blur handler can revert it
    // if the approval gate fires and the operator cancels the dialog.
    porcentajeSnapshotRef.current = porcentaje;
  };

  const handlePorcentajeChange = (next: number) => {
    // Free-typing — no gate. The gate runs on blur so typing "5" en
    // route to "50" doesn't open the dialog on the intermediate "5"
    // and block the user from completing the input.
    porcentajeDirtyRef.current = true;
    setPorcentaje(next);
    setDirtyMedios(true);
  };

  const handlePorcentajeBlur = () => {
    // MX-only approval gate (AR returns umbral=0 → branch unreachable).
    // If the finalised value is below the umbral AND differs from the
    // value the operator started from, hold the request in
    // `pendingLowPorcentaje` and pop the dialog. We revert the input
    // to the pre-edit snapshot up-front so cancelling the dialog leaves
    // the field at the previous value; confirming re-applies the typed
    // value inside the dialog's onClick handler.
    if (
      factPctEnabled &&
      porcentaje < factPctUmbral &&
      porcentaje !== porcentajeSnapshotRef.current &&
      factPctPending === null
    ) {
      const requested = porcentaje;
      setPorcentaje(porcentajeSnapshotRef.current);
      setPendingLowPorcentaje(requested);
    }
  };

  const saveMediosMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('no id');
      const payload = buildSaveMediosPayload({
        moneda,
        breakdown,
        porcentaje,
        medioRows,
        pagosActivos: pagosQ.data ?? [],
        homeCurrency,
      });
      return saveMediosPago(id, payload);
    },
    onSuccess: () => {
      setDirtyMedios(false);
      // Saved → header refetch becomes the new source of truth for
      // initialPorcentaje, so drop the dirty guard.
      porcentajeDirtyRef.current = false;
      // Lift the post-acceptance gate — once medios + % factura are
      // saved the venta is in a consistent state and the user can
      // navigate away normally.
      setRequiresPostAcceptanceSave(false);
      toast.success('Medios de pago guardados');
      qc.invalidateQueries({ queryKey: ['pagos'] });
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('[pagos] saveMediosPago failed', err);
      if (err instanceof ApiError && err.code === 'pending_discount_approval') {
        setErrorMsg(
          err.message ||
            'Esta venta tiene un descuento pendiente de aprobación. Esperá a que se resuelva.',
        );
        return;
      }
      setErrorMsg('No se pudieron guardar los medios de pago.');
    },
  });

  // Revert the post-acceptance flip when the user bails before saving.
  // Mirror of the Aceptar mutation — flips Status_RO back to Cotizado
  // and clears Aceptado_RO. The block-then-revert flow is wired below
  // via `useBlocker`.
  const revertAceptacionMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('no id');
      return resumenOperacionesClient.updateItem(id, {
        Status_RO: 'Cotizado',
        Aceptado_RO: 'NO',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
    },
  });

  // Reconcile the `requiresPostAcceptanceSave` flag against the actual
  // venta state. `location.state.justAccepted` is sticky across hard
  // reloads (browser preserves history.state), so a user who saved
  // medios, registered cobros, then reloaded the page would re-arm
  // the flag and get the "Salir sin guardar" dialog despite the
  // venta being well past Aceptado. Clear the flag when:
  //   - Status_RO has moved past Aceptado (e.g. Pendiente de Pago,
  //     Confirmacion de Pago, Pago Parcial, …) — proves the user
  //     registered at least one pago, which only happens after
  //     medios were saved.
  //   - There's at least one non-Anulado row in 03.Pagos (medios are
  //     saved but no comprobante registered yet — covers the "saved
  //     medios then reloaded while still at Aceptado" window).
  //   - A factura% approval is Pendiente: the page is read-only while
  //     admin reviews, so blocking navigation would trap the seller
  //     (they can't save either). The approval itself preserves the
  //     work-in-progress server-side; we don't lose anything by
  //     letting them leave. Found 2026-05-19 — QA could neither save
  //     nor exit a venta sitting at "Esperando aprobación".
  useEffect(() => {
    if (!requiresPostAcceptanceSave) return;
    const currentStatus = headerQ.data?.fields.Status_RO ?? null;
    if (currentStatus && currentStatus !== 'Aceptado') {
      setRequiresPostAcceptanceSave(false);
      return;
    }
    const hasActivePagos = (pagosQ.data ?? []).some(
      (p) => (p.fields.Status_P ?? '') !== 'Anulado',
    );
    if (hasActivePagos) {
      setRequiresPostAcceptanceSave(false);
      return;
    }
    if (factPctPending !== null) {
      setRequiresPostAcceptanceSave(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresPostAcceptanceSave, headerQ.data, pagosQ.data, factPctPending]);

  // Block in-app navigation away from this page until the user has
  // either saved medios + % factura or explicitly confirmed they want
  // to bail (in which case we revert the venta to Cotizado below).
  // Captures the back button, sidebar nav links, logout-induced
  // redirects — anything that goes through React Router.
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    requiresPostAcceptanceSaveRef.current &&
    currentLocation.pathname !== nextLocation.pathname,
  );

  // Tab-close / refresh / address-bar navigation doesn't go through
  // React Router, so `useBlocker` misses it. The native
  // `beforeunload` prompt is the only client-side signal we can hook
  // — the message itself is browser-controlled (Chrome/Firefox/Safari
  // ignore custom strings) but `preventDefault()` still triggers
  // their generic "Leave site?" confirmation. Note this is a polite
  // warning only: even if the user clicks Leave we can't reliably
  // run the revert, so the Aceptado status will stick. A reconcile
  // cron is the proper durable fix.
  useEffect(() => {
    if (!requiresPostAcceptanceSave) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Setting returnValue is required by older browsers (Edge,
      // Safari) for the prompt to actually appear.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [requiresPostAcceptanceSave]);

  // Facturación previa pedida desde una fila ya registrada (el otro punto de
  // entrada es el toggle del diálogo de alta). AR-only.
  const facturacionPreviaMutation = useMutation({
    mutationFn: (comprobanteId: string) =>
      solicitarFacturacionPrevia(id ?? '', comprobanteId),
    onSuccess: (r) => {
      if (r.alreadyApproved) {
        toast.info('Este cobro ya tiene la Factura Anticipada aprobada');
      } else if (r.alreadyPending) {
        toast.info('Ya hay una solicitud pendiente para este cobro');
      } else {
        toast.success('Solicitud de Factura Anticipada enviada', {
          description: 'Un administrador tiene que aprobarla desde Aprobaciones.',
        });
      }
      qc.invalidateQueries({ queryKey: ['pagos'] });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      setConfirmPrevia(null);
    },
    onError: (err) => {
      toast.error('No se pudo pedir la Factura Anticipada', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });

  const registrarMutation = useMutation({
    mutationFn: async ({
      kind,
      pagos,
    }: {
      kind: 'pago' | 'promesa';
      pagos: StagedPago[];
    }) => {
      if (!id) throw new Error('no id');
      // Iterate the staged queue. Each entry → one ComprobantePagos row
      // followed by an optional file upload. Per-row failures are
      // captured so a single bad row doesn't lose the others.
      const rowFailures: string[] = [];
      const fileFailures: string[] = [];
      // Articulo-level shortage list returned by the server (409
      // `stock_insuficiente`). Aggregated across all pagos in the
      // queue so a multi-medio submit reports every conflict, not
      // just the first.
      type Shortage = {
        articuloId: string;
        articulo: string;
        requerido: number;
        disponible: number;
      };
      const stockShortages: Shortage[] = [];
      let successCount = 0;
      // Facturación previa (AR): solicitudes enviadas y las que fallaron.
      let previaRequested = 0;
      const previaFailures: string[] = [];
      // Venta al 100% y un pago sin factura: el server lo rechaza con su
      // propio mensaje (el diálogo ya lo traba; esto cubre un cliente viejo).
      let requiereFacturaMsg: string | null = null;
      for (const p of pagos) {
        const payload: RegistrarComprobantePayload = {
          kind,
          medioPago: p.medioPago,
          medioPagoId: p.medioPagoId,
          categoria: p.categoria,
          monto: p.monto,
          tipoCambio: p.tipoCambio,
          pesos: p.pesos,
          fechaCobro: p.fechaCobro,
          fechaPago: p.fechaPago,
          esPagoFinal: p.esPagoFinal,
          contieneFactura: p.contieneFactura,
          echeq: p.echeq,
        };
        let comprobanteId: string;
        try {
          const res = await registrarComprobante(id, payload);
          comprobanteId = res.comprobanteId;
          successCount += 1;
        } catch (err) {
          // Pending discount approval: abort the whole batch — the
          // server will reject every row identically, so spamming
          // toasts per medio is noise. Bubble to the mutation's
          // onError where a single setErrorMsg fires.
          if (err instanceof ApiError && err.code === 'pending_discount_approval') {
            throw err;
          }
          if (err instanceof ApiError && err.code === 'stock_insuficiente') {
            const det = err.details as { shortages?: Shortage[] } | undefined;
            for (const s of det?.shortages ?? []) {
              if (!stockShortages.some((existing) => existing.articuloId === s.articuloId)) {
                stockShortages.push(s);
              }
            }
            // Surface a row failure too so the submit doesn't claim
            // success — but keep the dedicated shortage list for the
            // toast below.
            rowFailures.push(p.medioPago);
            continue;
          }
          // También los rechazos del E-cheq (duplicado, fecha fuera de rango): su mensaje dice qué corregir.
          if (err instanceof ApiError && (err.code === 'requiere_factura' || err.code?.startsWith('echeq_'))) {
            requiereFacturaMsg = err.message;
            rowFailures.push(p.medioPago);
            continue;
          }
          // eslint-disable-next-line no-console
          console.error('[pagos] registrarComprobante failed for', p.medioPago, err);
          rowFailures.push(p.medioPago);
          continue;
        }
        if (p.file) {
          try {
            await uploadDocument({
              entityType: 'ComprobanteVenta',
              entityId: comprobanteId,
              file: p.file,
            });
          } catch {
            fileFailures.push(p.file.name);
          }
        }
        // Facturación previa: el comprobante ya existe, ahora pedimos la
        // autorización para facturarlo antes de cobrarlo. Va después del
        // alta (necesita el id) y se trata como el upload: si falla, el
        // cobro igual quedó registrado, así que lo avisamos aparte en vez
        // de tirar toda la fila.
        if (p.facturacionPrevia) {
          try {
            await solicitarFacturacionPrevia(id, comprobanteId);
            previaRequested += 1;
          } catch {
            previaFailures.push(p.medioPago);
          }
        }
      }
      return {
        successCount,
        rowFailures,
        fileFailures,
        stockShortages,
        previaRequested,
        previaFailures,
        requiereFacturaMsg,
      };
    },
    onSuccess: (
      {
        successCount,
        rowFailures,
        fileFailures,
        stockShortages,
        previaRequested,
        previaFailures,
        requiereFacturaMsg,
      },
      { kind },
    ) => {
      setRegistrarKind(null);
      // Stock-insuficiente takes precedence over the generic row
      // failure message — admins need to see WHICH articulos are
      // short so they can decide whether to anular this venta or
      // wait for the conflicting one to be cancelled.
      if (stockShortages.length > 0) {
        const lines = stockShortages
          .map(
            (s) =>
              `${s.articulo}: necesario ${s.requerido}, disponible ${s.disponible}`,
          )
          .join(' · ');
        setErrorMsg(
          `Stock insuficiente. Otro pago consumió las unidades reservadas. ${lines}`,
        );
        qc.invalidateQueries({ queryKey: ['pagos'] });
        qc.invalidateQueries({ queryKey: ['detalle-venta'] });
        qc.invalidateQueries({ queryKey: ['ventas'] });
        return;
      }
      if (rowFailures.length > 0) {
        setErrorMsg(
          requiereFacturaMsg
            ? `${requiereFacturaMsg} No se registró: ${rowFailures.join(', ')}.`
            : `No se pudieron registrar algunos pagos (${rowFailures.join(', ')}). Reintentá los faltantes.`,
        );
        // Los que sí entraron cambian el cupo: refrescar para que el
        // diálogo, al reabrir, ya venga con el toggle trabado.
        qc.invalidateQueries({ queryKey: ['pagos'] });
        return;
      }
      if (fileFailures.length > 0) {
        toast.warning('No se pudieron subir todos los comprobantes', {
          description: fileFailures.join(', '),
        });
      }
      if (previaFailures.length > 0) {
        toast.warning('No se pudo pedir la Factura Anticipada', {
          description: `${previaFailures.join(', ')}. El pago quedó registrado; pedila desde la tabla de pagos.`,
        });
      } else if (previaRequested > 0) {
        // Recién creadas: la cola de Aprobaciones y el badge del sidebar
        // tienen que verlas sin esperar al poll de 15s.
        qc.invalidateQueries({ queryKey: ['aprobaciones'] });
        toast.success(
          previaRequested === 1
            ? 'Solicitud de Factura Anticipada enviada'
            : `${previaRequested} solicitudes de Factura Anticipada enviadas`,
          { description: 'Un administrador tiene que aprobarla desde Aprobaciones.' },
        );
      }
      const noun = kind === 'pago' ? 'Pago' : 'Promesa';
      const verb = successCount === 1 ? 'registrada' : 'registradas';
      const plural = successCount === 1 ? '' : 's';
      setSuccessMsg(`${successCount} ${noun}${plural} ${verb} correctamente.`);
      qc.invalidateQueries({ queryKey: ['pagos'] });
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      // Admin queues read sales_payment_receipts too — mark them
      // stale so navigating to Cobros / Cuentas por Cobrar / etc.
      // immediately refetches instead of showing the cached list.
      qc.invalidateQueries({ queryKey: ['administracion'] });
    },
    onError: (err) => {
      setRegistrarKind(null);
      // eslint-disable-next-line no-console
      console.error('[pagos] registrarComprobante failed', err);
      if (err instanceof ApiError && err.code === 'pending_discount_approval') {
        setErrorMsg(
          err.message ||
            'Esta venta tiene un descuento pendiente de aprobación. Esperá a que se resuelva antes de registrar nuevos pagos.',
        );
        return;
      }
      setErrorMsg('No se pudo registrar el pago.');
    },
  });

  const anularComprobanteMutation = useMutation({
    mutationFn: async (c: ComprobantePagoItem) => {
      if (!id) throw new Error('no id');
      return anularComprobante(id, c.id);
    },
    onSuccess: () => {
      toast.success('Comprobante anulado');
      qc.invalidateQueries({ queryKey: ['pagos'] });
      qc.invalidateQueries({ queryKey: ['administracion'] });
      // Anular tiene dos efectos fuera de esta pantalla: el server anula la
      // solicitud de facturación previa pendiente (badge de Aprobaciones), y
      // si el cobro tenía CAE pasa a ser una factura huérfana (aviso de Home,
      // que lee `['facturacion']`). Sin invalidar, el badge sigue contando y
      // el aviso no aparece hasta que algo más refresque esas queries.
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['facturacion'] });
      setConfirmAnular(null);
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('[pagos] anularComprobante failed', err);
      // Surface the server's specific reason when present — e.g. the
      // Nova guard ('nova_already_confirmed': "Nova ya confirmó este
      // cobro. Para anularlo, coordiná un reverso…") — instead of a
      // generic message that hides what the operator must do next.
      setErrorMsg(
        err instanceof ApiError && err.message
          ? err.message
          : 'No se pudo anular el comprobante.',
      );
      setConfirmAnular(null);
    },
  });

  // Cobro con factura emitida: la anulación va a aprobación (Eliminaciones).
  const solicitarAnulacionMutation = useMutation({
    mutationFn: async (c: ComprobantePagoItem) => {
      if (!id) throw new Error('no id');
      return solicitarAnulacionComprobante(id, c.id);
    },
    onSuccess: (r) => {
      if (r.outcome === 'pending_approval_created') {
        toast.success('Solicitud de anulación enviada', {
          description: 'Un administrador tiene que aprobarla desde Aprobaciones.',
        });
      } else {
        toast.info('Ya hay una solicitud pendiente', {
          description: 'La anulación de este cobro ya está esperando aprobación.',
        });
      }
      qc.invalidateQueries({ queryKey: ['pagos'] });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['facturacion'] }); // aviso rojo de Home
      setConfirmAnular(null);
    },
    onError: (err) => {
      setErrorMsg(
        err instanceof ApiError && err.message
          ? err.message
          : 'No se pudo enviar la solicitud de anulación.',
      );
      setConfirmAnular(null);
    },
  });

  const cargarComprobanteMutation = useMutation({
    mutationFn: async ({
      comp,
      form,
    }: {
      comp: ComprobantePagoItem;
      form: SubirComprobanteFormValue;
    }) => {
      if (!id) throw new Error('no id');
      // Two-step: PATCH the form fields (medio/monto/fechaPago/contieneFactura)
      // first, then upload the file. The upload's `runPromesaUploadHook`
      // on the server handles Status_P → 'Pendiente de Confirmacion',
      // bumps Status_RO → 'Confirmacion de Pago', and consumes stock.
      await cargarComprobante(id, comp.id, {
        medioPago: form.medioPago,
        monto: form.monto,
        fechaPago: form.fechaPago,
        contieneFactura: form.contieneFactura,
      });
      await uploadDocument({
        entityType: 'ComprobanteVenta',
        entityId: comp.id,
        file: form.file,
      });
    },
    onSuccess: () => {
      setCargarComprobanteFor(null);
      toast.success('Comprobante cargado correctamente');
      qc.invalidateQueries({ queryKey: ['pagos'] });
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      qc.invalidateQueries({ queryKey: ['administracion'] });
    },
    onError: (err) => {
      // Block: a pending discount approval on the venta freezes new
      // uploads until admin resolves it (either approves or rejects
      // the discount). Surface the server's friendly message so the
      // operator knows to wait.
      if (err instanceof ApiError && err.code === 'pending_discount_approval') {
        setCargarComprobanteFor(null);
        setErrorMsg(
          err.message ||
            'Esta venta tiene un descuento pendiente de aprobación. Esperá a que se resuelva antes de cargar el comprobante.',
        );
        return;
      }
      // Stock-insuficiente from the upload route's pre-flight: another
      // pago drained the pool between the cotización being accepted
      // and this comprobante landing. The server didn't commit the
      // file or any row mutations, so the admin can free stock
      // (anular the conflicting venta or restock the article) and
      // retry from the same dialog.
      if (err instanceof ApiError && err.code === 'stock_insuficiente') {
        type Shortage = {
          articuloId: string;
          articulo: string;
          requerido: number;
          disponible: number;
        };
        const det = err.details as { shortages?: Shortage[] } | undefined;
        const lines = (det?.shortages ?? [])
          .map(
            (s) =>
              `${s.articulo}: necesario ${s.requerido}, disponible ${s.disponible}`,
          )
          .join(' · ');
        setCargarComprobanteFor(null);
        setErrorMsg(
          `Stock insuficiente. Otro pago consumió las unidades reservadas. ${lines}`,
        );
        return;
      }
      if (err instanceof ApiError && err.code === 'requiere_factura') {
        setErrorMsg(err.message);
        return;
      }
      // eslint-disable-next-line no-console
      console.error('[pagos] cargarComprobante failed', err);
      setErrorMsg('No se pudo cargar el comprobante. Intentá nuevamente.');
    },
  });

  const editarPromesaMutation = useMutation({
    mutationFn: async ({
      comp,
      form,
    }: {
      comp: ComprobantePagoItem;
      form: EditarPagoFormValue;
    }) => {
      if (!id) throw new Error('no id');
      const result = await editarPromesa(id, comp.id, {
        medioPago: form.medioPago,
        monto: form.monto,
        fechaCobro: form.fechaCobro,
        tipoCambio: form.tipoCambio,
        pesos: form.pesos,
      });
      // Pass the source row's status through so onSuccess can pick the
      // right copy — promesa vs pago pendiente de confirmación use the
      // same backend route but the user-facing label diverges.
      return { ...result, status: comp.fields.Status_P ?? '' };
    },
    onSuccess: (res) => {
      setEditarPromesaFor(null);
      const wasPromesa = res.status === 'Pendiente de Cobro';
      toast.success(wasPromesa ? 'Promesa actualizada' : 'Pago actualizado');
      qc.invalidateQueries({ queryKey: ['pagos'] });
      qc.invalidateQueries({ queryKey: ['administracion'] });
    },
    onError: (err, vars) => {
      // eslint-disable-next-line no-console
      console.error('[pagos] editarPromesa failed', err);
      const wasPromesa = vars.comp.fields.Status_P === 'Pendiente de Cobro';
      // 4xx/409 traen el motivo (factura emitida, estado, E-cheq): mostrarlo.
      setErrorMsg(
        err instanceof ApiError && err.status < 500 && err.message !== err.code
          ? err.message
          : wasPromesa
            ? 'No se pudo actualizar la promesa.'
            : 'No se pudo actualizar el pago.',
      );
    },
  });

  // --- early states ---
  if (headerQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }
  if (headerQ.isError || !header) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/ventas')} className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Volver
        </Button>
        <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center">
          <p className="text-base font-medium text-foreground">Operación no encontrada</p>
        </div>
      </div>
    );
  }

  const processing =
    saveMediosMutation.isPending ||
    registrarMutation.isPending ||
    anularComprobanteMutation.isPending;

  // Guardar is only meaningful when the medios distribution is coherent:
  //   - at least one medio is selected (saving zero rows makes no sense),
  //   - the sum covers the total within $1 tolerance (USD-with-TC math
  //     and percent splits often leave $0.01–$0.50 residue; demanding
  //     exact zero forced users into manual fudge edits with no value
  //     to the bookkeeping),
  //   - there are local changes (dirty), and
  //   - no save is already in flight.
  const PENDIENTE_TOLERANCE_ARS = 1;
  const anyMedioChecked = medioRows.some((r) => r.pagoUnico || r.distPagos);
  const pendienteResuelto = Math.abs(pendiente) <= PENDIENTE_TOLERANCE_ARS;
  // Sin el detalle cargado el desglose da $0 y se guardarían medios en $0
  // (pasó con un clic apenas abierta la pantalla). Lo mismo si el desglose da
  // $0 pero la venta tiene total: algo no cargó bien.
  const totalVenta = parseMoney(header?.fields.TotalCotizacion_RO) ?? 0;
  const detalleNoListo =
    !detallesQ.isSuccess || (totalVenta > 0 && breakdown.montoTotalCobro <= 0);
  const saveBlockReason: string | null = detalleNoListo
    ? 'Esperando el detalle de la venta…'
    : !dirtyMedios
    ? 'No hay cambios para guardar.'
    : !anyMedioChecked
      ? 'Seleccioná al menos un medio de pago para guardar.'
      : !pendienteResuelto
        ? `El pendiente debe estar dentro de ±${formatMoney(PENDIENTE_TOLERANCE_ARS, moneda)} para guardar.`
        : null;
  const canSaveMedios =
    !readOnly && saveBlockReason === null && !saveMediosMutation.isPending;

  // Registrar pago/promesa is only meaningful once the medios de pago
  // distribution has been saved AND covers the full total. Otherwise the
  // user is registering a receipt against an incomplete / stale setup.
  // Once comprobantes already cover the total we lock both buttons too —
  // matches PowerApps where Registrar pago/promesa hides on fully-cobrada
  // ventas.
  const registrarDisabledReason: string | undefined = (() => {
    if (readOnly) return undefined;
    if (dirtyMedios) return 'Guardá los medios de pago antes de registrar un pago.';
    // Same ±$1 tolerance as save — keeps the save and registrar gates
    // consistent so a save accepted within rounding-error never traps
    // the user with a registrar that still demands exact cobertura.
    if (Math.abs(pendiente) > PENDIENTE_TOLERANCE_ARS) {
      return 'Los medios de pago deben cubrir el Total antes de registrar pagos.';
    }
    if (medioRows.every((r) => !r.pagoUnico && !r.distPagos)) {
      return 'Seleccioná al menos un medio de pago antes de registrar pagos.';
    }
    if (pagadoPorComprobantes >= totalCobro - 0.01) {
      return 'La venta ya fue cobrada en su totalidad.';
    }
    return undefined;
  })();

  const copiarMetodos = async () => {
    const text = buildCopiarMetodosText(medioRows, moneda, homeCurrency);
    if (!text) {
      toast.info('No hay medios seleccionados con monto cargado.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Métodos copiados al portapapeles.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[pagos] clipboard write failed', err);
      toast.error('No se pudo copiar al portapapeles.');
    }
  };

  return (
    // Whole-page scroll: the page is taller than the viewport on
    // smaller screens (especially macOS where browser chrome is
    // taller). Letting the page scroll naturally inside the AppShell's
    // overflow-hidden main avoids the cramped fixed-height layout
    // where Carga de pagos got squeezed to a single row.
    <div className="flex h-full flex-col gap-3 overflow-y-auto overflow-x-hidden">
      {/* TOP TOOLBAR — title row + venta metadata strip. The metadata
          line carries cliente, Nº cotización, fecha, vendedor and
          moneda inline so the user has the operación's identity in
          the header instead of buried inside the breakdown card. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1">
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          onClick={() => {
            // Nova hits this screen from the /ventas eye icon (fromVentasList)
            // and has no access to the editable /ventas/:id detail page, so
            // route them back to the list instead of the default detail.
            if (fromAdmin) return navigate(adminBackUrl);
            if (fromVentasList || isNova) return navigate('/ventas');
            navigate(`/ventas/${id}`);
          }}
          aria-label={
            fromAdmin
              ? 'Volver a Administración'
              : fromVentasList || isNova
                ? 'Volver a Ventas'
                : 'Volver al detalle'
          }
          title={
            fromAdmin
              ? 'Volver a Administración'
              : fromVentasList || isNova
                ? 'Volver a Ventas'
                : 'Volver al detalle'
          }
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Pagos</h1>
        <div className="ml-auto flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
          {header.fields.Cliente_RO?.trim() && (
            <span className="font-semibold text-foreground">
              {header.fields.Cliente_RO.trim()}
            </span>
          )}
          {isExento && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                  IVA Exento
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-medium">Cliente IVA Exento</p>
                <p className="mt-0.5 text-xs leading-snug">
                  Este cliente está exento de IVA. Los pagos pueden facturarse
                  por el total de la venta aunque el porcentaje de facturación
                  sea 0%.
                </p>
              </TooltipContent>
            </Tooltip>
          )}
          <Sep show={Boolean(header.fields.Cliente_RO?.trim())} />
          <span className="text-muted-foreground">
            Nº {header.fields.NrCotizacion_RO ?? header.id}
          </span>
          {header.fields.Fecha_RO && (
            <>
              <Sep />
              <span className="text-muted-foreground">{header.fields.Fecha_RO}</span>
            </>
          )}
          {header.fields.Vendedor_RO && (
            <>
              <Sep />
              <span className="text-muted-foreground">{header.fields.Vendedor_RO}</span>
            </>
          )}
          <Sep />
          <span className="text-muted-foreground">{moneda}</span>
        </div>
      </div>

      {factPctPending && (
        <div className="flex shrink-0 items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0">
            <div className="font-medium">
              Esperando aprobación del porcentaje de facturación
            </div>
            <div className="text-xs text-amber-800">
              Se solicitó {factPctPending.porcentaje_solicitado}% (mínimo
              permitido {factPctPending.umbral}%). La pantalla queda bloqueada
              hasta que un administrador resuelva la solicitud desde
              Aprobaciones.
            </div>
          </div>
        </div>
      )}

      {terminalStatus && !factPctPending && (
        <div className="flex shrink-0 items-start gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0">
            <div className="font-medium">
              Esta venta está en estado "{terminalStatus}" — sólo lectura.
            </div>
            <div className="text-xs text-slate-700">
              {terminalStatus === 'Anulado'
                ? 'La venta fue anulada. No se pueden modificar los medios de pago ni el porcentaje de facturación.'
                : 'La venta ya pasó la etapa de pagos. Para modificar los medios o el porcentaje, un administrador debe revertir el estado primero.'}
            </div>
          </div>
        </div>
      )}

      <EcheqDespachoAviso ventaId={id!} status={status} />

      {/* TWO-COLUMN BODY: left stack = breakdown + medios (stacked
          vertically, no extra space between cards); right column =
          Carga de pagos at full body height. The carga column owns
          its own scroll so the breakdown stays anchored. */}
      <div className="flex flex-col gap-3 lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:grid-rows-1 lg:items-stretch">
        <div className="flex min-w-0 flex-col gap-3 lg:min-h-0">
          <InvoiceBreakdownCard
            porcentaje={porcentaje}
            onPorcentajeChange={handlePorcentajeChange}
            onPorcentajeFocus={handlePorcentajeFocus}
            onPorcentajeBlur={handlePorcentajeBlur}
            breakdown={breakdown}
            moneda={moneda}
            readOnly={readOnly}
            usado={wouldBeUsado}
          />
          {/* El recargo va sobre la parte con factura: con 0% no hay sobre qué aplicarlo. */}
          {recargoActivo && breakdown.totalFactura <= 0 && (
            <div className="flex shrink-0 items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <div className="min-w-0">
                <div className="font-medium">
                  MercadoPago en {recargoActivo.cuotas} cuotas sin recargo
                </div>
                <div className="text-xs text-amber-800">
                  El recargo se aplica sobre la parte con factura. Con 0% de factura no se suma
                  nada.
                </div>
              </div>
            </div>
          )}
          <MediosDePagoTable
            rows={medioRows}
            onRowPatch={onRowPatch}
            moneda={moneda}
            pendiente={pendiente}
            total={totalCobro}
            onCopiarMetodos={copiarMetodos}
            canCopiar={medioRows.some(
              (r) => (r.pagoUnico || r.distPagos) && (parseMoney(r.monto) ?? 0) > 0,
            )}
            readOnly={readOnly || !detallesQ.isSuccess}
            pagadoSegunMedios={pagadoTotal}
            totalCobro={totalCobro}
            ventaEnFlujoNova={isFNova}
            opcionesCuotas={opcionesCuotas}
            guardarSlot={
              <Button
                type="button"
                size="icon"
                onClick={() => {
                  saveMediosMutation.mutate();
                }}
                disabled={!canSaveMedios}
                title={readOnly ? undefined : saveBlockReason ?? 'Guardar medios de pago'}
                aria-label="Guardar medios de pago"
                className="h-[3.25rem] w-[3.25rem] bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <Save className="h-5 w-5" aria-hidden />
              </Button>
            }
          />
        </div>

        <ComprobantePagosTable
          comprobantes={comprobantesQ.data ?? []}
          moneda={moneda}
          homeCurrency={homeCurrency}
          onRegistrarPago={() => setRegistrarKind('pago')}
          onRegistrarPromesa={() => setRegistrarKind('promesa')}
          onVerComprobante={(c) => setVerComprobante(c)}
          onAnularComprobante={(c) => setConfirmAnular(c)}
          onCargarComprobante={(c) => setCargarComprobanteFor(c)}
          onEditarPromesa={(c) => setEditarPromesaFor(c)}
          recargoMp={recargoMpVenta}
          previaEstadoByComprobante={previaEstadoByComprobante}
          echeqByComprobante={echeqByComprobante}
          previaMotivoRechazoByComprobante={previaMotivoRechazoByComprobante}
          onSolicitarFacturacionPrevia={
            countryCode === 'AR' ? (c) => setConfirmPrevia(c) : undefined
          }
          readOnly={readOnly}
          registrarDisabledReason={registrarDisabledReason}
          summary={{
            disponibleAFacturar,
            pagado: cobradoPorComprobantes,
            pendiente: round2(totalCobro - cobradoPorComprobantes),
            total: totalCobro,
          }}
        />
      </div>

      <RegistrarPagoDialog
        open={registrarKind !== null && !registrarMutation.isPending}
        kind={registrarKind ?? 'pago'}
        ventaMoneda={moneda}
        mediosDePago={mediosForVentaPicker}
        // Remaining-to-collect, not the gross total: if comprobantes are
        // already in flight (Pendiente de Confirmación / Pendiente de
        // Cobro / Confirmado) the "Pago Único" toggle should fill what's
        // still owed, otherwise the user would over-collect.
        totalACobrar={Math.max(0, round2(totalCobro - pagadoPorComprobantes))}
        recargoMp={recargoMpVenta}
        disponibleAFacturar={disponibleAFacturar}
        disponibleSinFactura={disponibleSinFactura}
        isExento={isExento}
        clienteFiscalMissing={clienteFiscalMissing}
        clienteCuit={contactoQ.data?.fields.CUIT_C?.trim() ?? ''}
        loading={registrarMutation.isPending}
        onCancel={() => setRegistrarKind(null)}
        onSubmit={(pagos) =>
          registrarKind && registrarMutation.mutate({ kind: registrarKind, pagos })
        }
      />
      {cargarComprobanteFor && (
        <SubirComprobanteDialog
          open
          comp={cargarComprobanteFor}
          ventaMoneda={moneda}
          mediosDePago={mediosForVentaPicker}
          disponibleAFacturar={disponibleAFacturar}
          disponibleSinFactura={disponibleSinFacturaSin(cargarComprobanteFor)}
          isExento={isExento}
          clienteFiscalMissing={clienteFiscalMissing}
          loading={cargarComprobanteMutation.isPending}
          onCancel={() => setCargarComprobanteFor(null)}
          onSubmit={(form) =>
            cargarComprobanteMutation.mutate({ comp: cargarComprobanteFor, form })
          }
        />
      )}
      {editarPromesaFor && (
        <EditarPagoDialog
          open
          comp={editarPromesaFor}
          mediosDePago={mediosForVentaPicker}
          mode={
            editarPromesaFor.fields.Status_P === 'Pendiente de Confirmacion'
              ? 'confirmacion'
              : 'promesa'
          }
          loading={editarPromesaMutation.isPending}
          ventaMoneda={moneda}
          // Available factura budget for the edited pago = global
          // disponible PLUS this pago's prior factura contribution, so
          // the user is treating the edit as a replace (not a stack).
          disponibleAFacturar={round2(
            disponibleAFacturar +
              sumComprobantesActivos([editarPromesaFor], moneda, {
                requireFactura: true,
              }),
          )}
          disponibleSinFactura={disponibleSinFacturaSin(editarPromesaFor)}
          isExento={isExento}
          clienteFiscalMissing={clienteFiscalMissing}
          onCancel={() => setEditarPromesaFor(null)}
          onSubmit={(form) =>
            editarPromesaMutation.mutate({ comp: editarPromesaFor, form })
          }
        />
      )}
      <SuccessDialog
        open={successMsg !== null}
        message={successMsg ?? ''}
        onClose={() => setSuccessMsg(null)}
      />
      <ErrorDialog
        open={errorMsg !== null}
        message={errorMsg ?? ''}
        onClose={() => setErrorMsg(null)}
      />
      {/* Low porcentaje factura — admin approval required. */}
      <Dialog
        open={pendingLowPorcentaje !== null}
        onOpenChange={(o) => {
          if (!o && !requestFactPctMutation.isPending) {
            setPendingLowPorcentaje(null);
            setPendingLowMotivo('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Requiere aprobación</DialogTitle>
            <DialogDescription>
              El porcentaje de facturación ({pendingLowPorcentaje}%) está por
              debajo del mínimo permitido ({factPctUmbral}%). Para continuar,
              un administrador debe aprobar la solicitud. Mientras tanto, la
              venta queda bloqueada para edición.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {pendingWouldBeUsado && usadoCtx && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
                <span className="font-semibold uppercase tracking-wide text-rose-700">
                  ⚠ Advertencia ·
                </span>{' '}
                Con {pendingLowPorcentaje}% la venta se registrará como{' '}
                <strong>usado</strong>.
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="fact-pct-motivo" className="text-sm">
                Motivo <span className="text-muted-foreground">(opcional)</span>
              </Label>
              <Textarea
                id="fact-pct-motivo"
                value={pendingLowMotivo}
                onChange={(e) => setPendingLowMotivo(e.target.value)}
                placeholder="Justificación que verá el administrador…"
                rows={3}
                maxLength={500}
                disabled={requestFactPctMutation.isPending}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPendingLowPorcentaje(null);
                setPendingLowMotivo('');
              }}
              disabled={requestFactPctMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (pendingLowPorcentaje == null) return;
                const value = pendingLowPorcentaje;
                const motivo = pendingLowMotivo;
                requestFactPctMutation.mutate(
                  { porcentajeSolicitado: value, motivo },
                  {
                    onSuccess: () => {
                      // Hold the local porcentaje at the requested value
                      // so the operator sees what they asked for while
                      // the request is pending.
                      porcentajeDirtyRef.current = true;
                      setPorcentaje(value);
                      setPendingLowPorcentaje(null);
                      setPendingLowMotivo('');
                    },
                  },
                );
              }}
              disabled={requestFactPctMutation.isPending}
            >
              {requestFactPctMutation.isPending ? 'Enviando…' : 'Solicitar aprobación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {processing && <ProcesandoOverlay message="Procesando…" />}
      {verComprobante &&
        (() => {
          // Re-derive from the live query so an upload/delete inside the
          // dialog flips the FileLink/FileUploader without needing a
          // dialog reopen. The state cell only carries the id; the latest
          // fields come from comprobantesQ.
          const live =
            (comprobantesQ.data ?? []).find((c) => c.id === verComprobante.id) ??
            verComprobante;
          return (
            <VerComprobanteDialog
              comp={live}
              onClose={() => setVerComprobante(null)}
              onRefresh={() => {
                qc.invalidateQueries({ queryKey: ['pagos'] });
              }}
              readOnly={readOnly}
            />
          );
        })()}
      {confirmPrevia && (
        <ConfirmarFacturacionPreviaDialog
          comp={confirmPrevia}
          ventaMoneda={moneda}
          // Tras un rechazo la acción sigue disponible a propósito (mismo
          // criterio que factura%), pero el diálogo tiene que decir que se
          // está RE-pidiendo algo que ya negaron, no estrenar la solicitud.
          yaRechazada={previaEstadoByComprobante.get(confirmPrevia.id ?? '') === 'Rechazado'}
          motivoRechazo={previaMotivoRechazoByComprobante.get(confirmPrevia.id ?? '')}
          loading={facturacionPreviaMutation.isPending}
          onCancel={() => setConfirmPrevia(null)}
          onConfirm={() => facturacionPreviaMutation.mutate(confirmPrevia.id)}
        />
      )}
      {confirmAnular && (
        <ConfirmarAnularComprobanteDialog
          comp={confirmAnular}
          ventaMoneda={moneda}
          loading={anularComprobanteMutation.isPending || solicitarAnulacionMutation.isPending}
          onCancel={() => setConfirmAnular(null)}
          onConfirm={() => anularComprobanteMutation.mutate(confirmAnular)}
          onSolicitar={() => solicitarAnulacionMutation.mutate(confirmAnular)}
        />
      )}
      {/* Post-acceptance leave-without-saving guard. The user landed
          here straight after Aceptar; if they try to navigate away
          before saving medios + % factura, we revert the venta to
          Cotizado so the system stays consistent. */}
      <PostAcceptanceLeaveGuard
        blocker={blocker}
        reverting={revertAceptacionMutation.isPending}
        onConfirmLeave={async () => {
          // Capture the target the user was trying to reach BEFORE
          // we reset — `blocker.reset()` clears `blocker.location`.
          const target = blocker.location;
          try {
            await revertAceptacionMutation.mutateAsync();
            // Lower the gate via the ref so the next blocker
            // callback returns false synchronously (state would
            // still be true on the very next render). Then reset
            // the blocker (instead of proceed) and navigate manually
            // — proceed() can leave router state stuck on v6 in
            // some scenarios, breaking subsequent navigations.
            setRequiresPostAcceptanceSave(false);
            blocker.reset?.();
            if (target) {
              navigate(target.pathname + (target.search ?? '') + (target.hash ?? ''));
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[pagos] revert aceptacion failed', err);
            setErrorMsg(
              'No se pudo revertir la cotización. Probá guardar los medios o reintentar.',
            );
            blocker.reset?.();
          }
        }}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}

type Blocker = ReturnType<typeof useBlocker>;

/**
 * Warning dialog shown when the user tries to leave the Pagos screen
 * after acceptance without first saving the medios de pago + % factura.
 * Renders as a controlled dialog driven by the router blocker — if
 * `blocker.state === 'blocked'` we show; "Salir sin guardar" reverts
 * the venta and proceeds, "Cancelar" stays put.
 */
function PostAcceptanceLeaveGuard({
  blocker,
  reverting,
  onConfirmLeave,
  onCancel,
}: {
  blocker: Blocker;
  reverting: boolean;
  onConfirmLeave: () => void;
  onCancel: () => void;
}) {
  const open = blocker.state === 'blocked';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !reverting && onCancel()}>
      <DialogContent
        className="sm:max-w-md"
        processing={reverting && 'Revirtiendo cotización…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            ¿Salir sin guardar?
          </DialogTitle>
          <DialogDescription>
            Todavía no guardaste los medios de pago ni el porcentaje de
            factura para esta cotización aceptada.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <p className="font-medium">Si continuás:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs leading-relaxed">
              <li>La venta volverá al estado <strong>Cotizado</strong>.</li>
              <li>Vas a tener que aceptarla de nuevo para registrar pagos.</li>
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={reverting}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirmLeave}
            disabled={reverting}
          >
            Salir sin guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerComprobanteDialog({
  comp,
  onClose,
  onRefresh,
  readOnly,
}: {
  comp: ComprobantePagoItem;
  onClose: () => void;
  onRefresh: () => void;
  readOnly: boolean;
}) {
  const f = comp.fields;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Comprobante #{comp.id}</DialogTitle>
          <DialogDescription>
            {cajaBase(f.MedioPago_CP ?? '—')} · Cargado {f.FechaHoraCarga_CP ?? f.Fecha_CP ?? '—'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Estado" value={f.Status_P} />
            <Info label="Monto" value={f.Monto_CP} />
            {f.NroFactura_CP && <Info label="Nº Factura" value={f.NroFactura_CP} />}
            {f.FechaCobro_CP && <Info label="Fecha cobro" value={f.FechaCobro_CP} />}
            {f.FechaPago_CP && <Info label="Fecha pago" value={f.FechaPago_CP} />}
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Comprobante de pago (imagen/PDF enviado por el cliente)
            </p>
            {f.ComprobanteDriveItemID_CP && f.ComprobanteFileName_CP ? (
              <FileLink
                entityType="ComprobanteVenta"
                entityId={comp.id}
                fileName={f.ComprobanteFileName_CP}
                onDeleted={onRefresh}
                allowDelete={!readOnly}
                // `readOnly` (prop) already folds in the parent's `canAct`
                // ownership decision — `!readOnly` ⇒ this user may write here.
                canWrite={!readOnly}
              />
            ) : readOnly ? (
              <p className="rounded-lg border border-dashed bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                Sin comprobante adjunto.
              </p>
            ) : (
              <FileUploader
                entityType="ComprobanteVenta"
                entityId={comp.id}
                onUploaded={onRefresh}
                emptyHint="Comprobante enviado por el cliente"
                // `readOnly` (prop) already folds in the parent's `canAct`
                // ownership decision — `!readOnly` ⇒ this user may write here.
                canWrite={!readOnly}
              />
            )}
          </div>

          {f.FacturaDriveItemID_CP && f.FacturaFileName_CP && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Factura emitida (solo lectura)
              </p>
              <FileLink
                entityType="FacturaVenta"
                entityId={comp.id}
                fileName={f.FacturaFileName_CP}
                allowDelete={false}
              />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-sm text-foreground">{value && value.trim() ? value : '—'}</p>
    </div>
  );
}

/**
 * Anti-misclick guard before we anular a comprobante. Triggered by the
 * trash icon on `ComprobantePagosTable` for both promesa rows
 * (`Pendiente de Cobro`) and pending-confirm rows (`Pendiente de
 * Confirmación`).
 */
/**
 * Confirmación de "Solicitar facturación previa".
 *
 * El botón de la fila mandaba la solicitud de una, sin preguntar. Es demasiado
 * barato para lo que dispara: si un admin la aprueba, sale una factura REAL a
 * ARCA por plata que todavía no entró, y lo único que la revierte es una Nota
 * de Crédito. El operador tiene que ver qué está pidiendo y sobre qué fila.
 */
function ConfirmarFacturacionPreviaDialog({
  comp,
  ventaMoneda,
  yaRechazada = false,
  motivoRechazo,
  loading,
  onCancel,
  onConfirm,
}: {
  comp: ComprobantePagoItem;
  ventaMoneda: Moneda;
  /** La última solicitud sobre este cobro fue rechazada: esto es un re-pedido. */
  yaRechazada?: boolean;
  /** Lo que escribió el administrador al rechazarla. */
  motivoRechazo?: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const f = comp.fields;
  const isPromesa = (f.Status_P ?? '') === 'Pendiente de Cobro';
  const medio = cajaBase(f.MedioPago_CP ?? '—'); // caja base, ver lib/cajasEfectivo
  const isUsdMedio =
    cajaBase(medio) === 'Efectivo Dolar' ||
    medio === 'Transferencia Galicia Dolar' ||
    medio === 'Transferencia Dolar';
  const displayAmount =
    ventaMoneda === 'ARS' && isUsdMedio
      ? parseMoney(f.Pesos_CP) ?? parseMoney(f.Monto_CP) ?? 0
      : parseMoney(f.Monto_CP) ?? 0;
  return (
    <Dialog open onOpenChange={(next) => !next && !loading && onCancel()}>
      <DialogContent className="max-w-md" processing={loading && 'Enviando solicitud…'}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
              <ReceiptText className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>
                {yaRechazada
                  ? 'Volver a pedir Factura Anticipada'
                  : 'Pedir Factura Anticipada'}
              </DialogTitle>
              <DialogDescription>
                {yaRechazada ? (
                  <>
                    Esta solicitud <strong>ya fue rechazada</strong> y la vas a
                    pedir de nuevo.
                  </>
                ) : (
                  <>
                    Vas a pedir autorización para emitir la factura{' '}
                    <strong>antes de recibir el pago</strong>.
                  </>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <div className="rounded-lg border bg-secondary px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Método</span>
              <span className="font-medium text-foreground">{medio}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">
                {isPromesa ? 'Fecha a cobrar' : 'Fecha de pago'}
              </span>
              <span className="font-medium text-foreground">
                {(isPromesa ? f.FechaCobro_CP : f.FechaPago_CP) ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Importe a facturar</span>
              <span className="font-medium text-foreground">
                {formatMoney(displayAmount, ventaMoneda)}
              </span>
            </div>
          </div>
          {yaRechazada && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              <p className="font-semibold uppercase tracking-wide text-amber-800">
                Solicitud rechazada anteriormente
              </p>
              <p className="mt-1">
                Un administrador ya rechazó esta solicitud. Volvé a enviarla
                sólo si cambiaron las condiciones que justifiquen una nueva
                revisión: va a reingresar a la cola de Aprobaciones para que se
                decida otra vez.
              </p>
              {motivoRechazo && (
                <p className="mt-2 border-t border-amber-300/70 pt-2">
                  <span className="font-semibold">Motivo del rechazo: </span>
                  {motivoRechazo}
                </p>
              )}
            </div>
          )}
          <div className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-900">
            <p className="font-semibold uppercase tracking-wide text-sky-800">
              Necesita aprobación de un administrador
            </p>
            <p className="mt-1">
              La solicitud queda pendiente en <strong>Aprobaciones</strong>: la
              factura no se emite hasta que la aprueben. Si la aprueban, el
              cobro aparece en Facturación para enviarlo a ARCA{' '}
              <strong>antes de que el cliente pague</strong>, y una vez emitida
              sólo una <strong>Nota de Crédito</strong> la revierte.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Volver
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="gap-2 bg-sky-600 text-white hover:bg-sky-700"
          >
            <ReceiptText className="h-4 w-4" aria-hidden />
            {yaRechazada ? 'Volver a pedir' : 'Pedir autorización'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmarAnularComprobanteDialog({
  comp,
  ventaMoneda,
  loading,
  onCancel,
  onConfirm,
  onSolicitar,
}: {
  comp: ComprobantePagoItem;
  ventaMoneda: Moneda;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Cobro con factura emitida: pedir la anulación a un administrador. */
  onSolicitar: () => void;
}) {
  const f = comp.fields;
  const isPromesa = (f.Status_P ?? '') === 'Pendiente de Cobro';
  // Con Facturación Previa un cobro puede tener la factura YA emitida y seguir
  // sin cobrarse. Anularlo directo dejaba el CAE vivo sin aprobación de nadie
  // (y la venta, ya "sin pagos", se eliminaba también directo), así que el
  // server lo rechaza (2026-09-17). El botón pasa a "Solicitar anulación":
  // la pide a un administrador por la cola de Eliminaciones.
  const facturaEmitida = f.Facturado_CP === 'SI';
  const medio = cajaBase(f.MedioPago_CP ?? '—'); // caja base, ver lib/cajasEfectivo
  const isUsdMedio =
    cajaBase(medio) === 'Efectivo Dolar' ||
    medio === 'Transferencia Galicia Dolar' ||
    medio === 'Transferencia Dolar';
  const displayAmount =
    ventaMoneda === 'ARS' && isUsdMedio
      ? parseMoney(f.Pesos_CP) ?? parseMoney(f.Monto_CP) ?? 0
      : parseMoney(f.Monto_CP) ?? 0;
  return (
    <Dialog open onOpenChange={(next) => !next && !loading && onCancel()}>
      <DialogContent
        className="max-w-md"
        processing={
          loading &&
          (facturaEmitida
            ? 'Enviando solicitud…'
            : isPromesa
              ? 'Anulando promesa…'
              : 'Anulando comprobante…')
        }
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>
                Anular {isPromesa ? 'promesa de pago' : 'comprobante'}
              </DialogTitle>
              <DialogDescription>
                Esta acción marcará el registro como anulado y no se puede
                deshacer.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <div className="rounded-lg border bg-secondary px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Método</span>
              <span className="font-medium text-foreground">{medio}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">
                {isPromesa ? 'Fecha a cobrar' : 'Fecha de pago'}
              </span>
              <span className="font-medium text-foreground">
                {(isPromesa ? f.FechaCobro_CP : f.FechaPago_CP) ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Monto</span>
              <span className="font-medium text-foreground">
                {formatMoney(displayAmount, ventaMoneda)}
              </span>
            </div>
          </div>
          {facturaEmitida && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              <p className="font-semibold uppercase tracking-wide text-amber-800">
                Tiene factura emitida
                {f.NroFactura_CP ? ` (${f.NroFactura_CP})` : ''}: requiere aprobación
              </p>
              <p className="mt-1">
                Anular el cobro <strong>no da de baja la factura</strong>: sigue
                vigente ante ARCA. La anulación la tiene que aprobar un
                administrador; una vez aprobada, la factura queda en el aviso
                de la pantalla principal hasta que se emita la{' '}
                <strong>Nota de Crédito</strong>.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Volver
          </Button>
          <Button
            type="button"
            onClick={facturaEmitida ? onSolicitar : onConfirm}
            disabled={loading}
            className="gap-2 bg-rose-600 text-white hover:bg-rose-700"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            {facturaEmitida ? 'Solicitar anulación' : 'Anular'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * Sum of medio rows the user has checked, in venta currency.
 *
 * `homeCurrency` is the active country's home (ARS/MXN); it determines
 * what "Local" medios are denominated in. Passed in by the caller
 * (the component) because helpers at module scope can't use hooks.
 */
/** Recargo MP vigente: la caja con cuotas, tildada y con cuotas elegidas. */
function recargoDeFilas(rows: MedioRow[]): { cuotas: number; pct: number } | null {
  const r = rows.find(
    (x) => x.admiteCuotas && (x.pagoUnico || x.distPagos) && x.cuotas && (x.recargoPct ?? 0) > 0,
  );
  return r ? { cuotas: r.cuotas!, pct: r.recargoPct! } : null;
}

/**
 * Parte (0..1) de la factura que cobra MercadoPago — el recargo va sólo sobre
 * esa parte. Sin otra caja no-efectivo tildada, MP cobra toda la factura (1).
 * Con otra: al rellenar montos, dos cajas en Dist. van 50/50 (½) y 3+ quedan
 * a mano (0 hasta que se cargue el monto); fuera del relleno se deduce del
 * monto de MP, que ya trae su recargo: M = s·N + s·F·p → s = M / (N + F·p),
 * con N = lo que cobran las cajas no-efectivo y F = total factura sin recargo.
 */
function parteFacturaMp(
  rows: MedioRow[],
  base: InvoiceBreakdown,
  pct: number,
  paraRellenar: boolean,
): number {
  const sel = rows.filter((x) => x.pagoUnico || x.distPagos);
  const mp = sel.find((x) => x.admiteCuotas);
  if (!mp) return 0;
  const otras = sel.filter((x) => x !== mp && x.tipo !== 'Efectivo');
  if (otras.length === 0) return 1;
  if (paraRellenar) return sel.length === 2 ? 0.5 : 0;
  const F = base.totalFactura;
  if (!(F > 0) || !(pct > 0)) return 0;
  const efectivo = sel
    .filter((x) => x.tipo === 'Efectivo')
    .reduce((a, x) => a + (parseMoney(x.monto) ?? 0), 0);
  const N = base.montoTotalCobro - efectivo;
  return (parseMoney(mp.monto) ?? 0) / (N + (F * pct) / 100);
}

/** Desglose con el recargo MP de estas filas (sobre la parte que cobra MP). */
function breakdownConRecargo(
  lineas: DetalleOperacionesItem[],
  porcentaje: number,
  rows: MedioRow[],
  paraRellenar = false,
): InvoiceBreakdown {
  const base = computeBreakdown(lineas, porcentaje, 0);
  const pct = recargoDeFilas(rows)?.pct ?? 0;
  return pct > 0
    ? computeBreakdown(lineas, porcentaje, pct, parteFacturaMp(rows, base, pct, paraRellenar))
    : base;
}

/**
 * Re-fill montos after the venta total moved (% factura or recargo MP):
 *   - Pago Único or single Dist. row -> new total
 *   - 2 Dist. rows -> factura/efectivo split (`montoForTwoDistRows`)
 *   - 3+ Dist. rows -> left alone (manual split)
 * Cross-currency rows are skipped — TC is required first.
 */
function rellenarMontos(
  prev: MedioRow[],
  breakdown: InvoiceBreakdown,
  isSameCurrency: (r: MedioRow) => boolean,
): MedioRow[] {
  const totalForFill = round2(breakdown.montoTotalCobro);
  const unicoRow = prev.find((r) => r.pagoUnico);
  if (unicoRow) {
    return prev.map((r) => {
      if (r.medioPagoId !== unicoRow.medioPagoId) return r;
      if (!isSameCurrency(r)) return r;
      return { ...r, monto: totalForFill > 0 ? String(totalForFill) : '' };
    });
  }
  const distRows = prev.filter((r) => r.distPagos);
  if (distRows.length === 1) {
    const targetId = distRows[0]!.medioPagoId;
    return prev.map((r) => {
      if (r.medioPagoId !== targetId) return r;
      if (!isSameCurrency(r)) return r;
      return { ...r, monto: totalForFill > 0 ? String(totalForFill) : '' };
    });
  }
  if (distRows.length === 2 && totalForFill > 0) {
    const montoForRow = montoForTwoDistRows(distRows, breakdown, totalForFill, isSameCurrency);
    return prev.map((r) => {
      if (!r.distPagos) return r;
      if (!isSameCurrency(r)) return r;
      return { ...r, monto: montoForRow.get(r.medioPagoId) ?? '' };
    });
  }
  return prev;
}

/**
 * Allocate monto across exactly two Dist. de Pagos rows.
 *
 * Default behaviour is a flat 50/50 split (`totalForFill / 2` each).
 * Special case: when exactly ONE of the two rows is Tipo='Efectivo'
 * (cash medio per `ABM_MediosDePago.Tipo_MDP`) AND both rows are
 * same-currency, the cash medio is assigned `montoCobroEfectivo`
 * (the un-invoiced portion, no IVA) and the other row is assigned
 * `totalFactura` (the invoiced portion + IVA). They sum to
 * `montoTotalCobro`, so the cobertura still hits 100% — but the
 * factura medio carries the IVA and the cash medio doesn't,
 * matching how the venta is actually invoiced.
 *
 * Example: Herconstru (Efectivo) + Nova Banorte (Banco) on a 50%-
 * factura MXN venta of 100k → Herconstru receives 50k cash, Nova
 * Banorte receives 50k + 16% IVA on the factura half.
 *
 * Returns a Map keyed by `medioPagoId`. Callers should still skip
 * cross-currency rows themselves (the helper assumes both rows are
 * same-currency for the efectivo-split path — when they aren't, it
 * falls back to the 50/50 default so the caller's same-currency
 * filter governs which rows actually get stamped).
 */
function montoForTwoDistRows(
  distRows: MedioRow[],
  breakdown: InvoiceBreakdown,
  totalForFill: number,
  isSameCurrency: (r: MedioRow) => boolean,
): Map<string, string> {
  const result = new Map<string, string>();
  if (distRows.length !== 2 || totalForFill <= 0) return result;
  const [a, b] = distRows as [MedioRow, MedioRow];
  const aIsEfectivo = a.tipo === 'Efectivo';
  const bIsEfectivo = b.tipo === 'Efectivo';
  // Exactly one efectivo medio AND both rows same-currency → split by
  // breakdown (cash → montoCobroEfectivo, other → totalFactura).
  if (aIsEfectivo !== bIsEfectivo && isSameCurrency(a) && isSameCurrency(b)) {
    const efectivoRow = aIsEfectivo ? a : b;
    const facturaRow = aIsEfectivo ? b : a;
    result.set(efectivoRow.medioPagoId, String(round2(breakdown.montoCobroEfectivo)));
    result.set(facturaRow.medioPagoId, String(round2(breakdown.totalFactura)));
    return result;
  }
  // Default: 50/50. Con MercadoPago en cuotas, su recargo (sobre su mitad de
  // la factura) va entero en la fila de MP.
  const mp = a.admiteCuotas ? a : b.admiteCuotas ? b : null;
  if (mp && breakdown.recargoMp > 0) {
    const otra = round2((totalForFill - breakdown.recargoMp) / 2);
    result.set(mp.medioPagoId, String(round2(totalForFill - otra)));
    result.set((mp === a ? b : a).medioPagoId, String(otra));
    return result;
  }
  const half = String(round2(totalForFill / 2));
  result.set(a.medioPagoId, half);
  result.set(b.medioPagoId, half);
  return result;
}

function computePagadoTotals(
  rows: MedioRow[],
  moneda: Moneda,
  homeCurrency: 'ARS' | 'MXN',
) {
  let pagadoTotal = 0;
  for (const r of rows) {
    if (!r.pagoUnico && !r.distPagos) continue;
    // parseMoney handles both dot and comma decimals — users type "132,60"
    // on es-AR keyboards and "132.60" on US keyboards.
    const monto = parseMoney(r.monto) ?? 0;
    const tc = parseMoney(r.tipoCambio) ?? 0;
    const medioMoneda = r.categoria === 'Internacional' ? 'USD' : homeCurrency;
    if (medioMoneda === moneda) {
      pagadoTotal += monto;
      continue;
    }
    // Cross-currency: monto is denominated in the caja's currency, not
    // the venta's. We can't fold it into pagadoTotal until tipo de
    // cambio is supplied — otherwise typing "150.000" on an MXN caja
    // for a USD venta inflated pagadoTotal as if 150K were USD,
    // pushing pendiente to −US$ 142K and the cobertura bar to a
    // visually-paid 100% (the ratio caps at 1).
    if (tc <= 0) continue;
    const inVentaMoneda =
      moneda === 'ARS' || moneda === 'MXN' ? monto * tc : monto / tc;
    pagadoTotal += inVentaMoneda;
  }
  return { pagadoTotal: round2(pagadoTotal) };
}

/**
 * Build the WhatsApp/clipboard summary that "Copiar Métodos" emits.
 *
 * Matches the PowerApps `btn_CopiarMetodosPago_P.OnSelect` formula
 * verbatim: one line per checked medio with the captured monto, the
 * cross-currency conversion when applicable, and the bank/account
 * block for transferencias and MercadoPago. Pago Único rows are
 * prefixed with "Pago único".
 */
function buildCopiarMetodosText(
  rows: MedioRow[],
  moneda: Moneda,
  homeCurrency: 'ARS' | 'MXN',
): string {
  const blocks: string[] = [];
  for (const r of rows) {
    if (!r.pagoUnico && !r.distPagos) continue;
    const monto = parseMoney(r.monto) ?? 0;
    if (monto <= 0) continue;
    const medioMoneda: Moneda = r.categoria === 'Internacional' ? 'USD' : homeCurrency;
    const tc = parseMoney(r.tipoCambio) ?? 0;
    const prefix = r.pagoUnico ? 'Pago único ' : '';
    const enCuotas = r.admiteCuotas && r.cuotas ? ` en ${r.cuotas} cuotas` : '';
    let line = `${prefix}${r.medio}${enCuotas}: ${formatMoney(monto, medioMoneda)}`;
    if (medioMoneda !== moneda && tc > 0) {
      const converted = medioMoneda === 'USD' ? monto * tc : monto / tc;
      const otherMoneda: Moneda = medioMoneda === 'USD' ? homeCurrency : 'USD';
      const otherLabel = otherMoneda === 'USD' ? 'Dólares' : 'Pesos';
      line +=
        ` Tipo de Cambio: ${tc.toLocaleString('es-AR', { maximumFractionDigits: 2 })}` +
        ` ${otherLabel}: ${formatMoney(converted, otherMoneda)}`;
    }
    const bank = bankBlockFor(r.medio);
    if (bank) line += `\n${bank}`;
    blocks.push(line);
  }
  return blocks.join('\n\n');
}

const BANK_BLOCK_CBU =
  'Banco Galicia\nRODRIGO MARTIN RIZZO\nCTA: 4023251-3 066-9\nCBU: 00700665-30004023251394\nALIAS: RorizzoCA\nCUIL: 20-34390469-0';
const BANK_BLOCK_CVU =
  'Banco Galicia\nRODRIGO MARTIN RIZZO\nCTA: 4023251-3 066-9\nCVU: 00700665-30004023251394\nALIAS: RorizzoCA\nCUIL: 20-34390469-0';

function bankBlockFor(medio: string): string | null {
  const m = medio.trim();
  if (m === 'MercadoPago') return BANK_BLOCK_CVU;
  if (m.startsWith('Transferencia')) return BANK_BLOCK_CBU;
  return null;
}

function sumComprobantesActivos(
  rows: ComprobantePagoItem[],
  moneda: Moneda,
  opts: { requireFactura?: boolean; excludePromesas?: boolean; sinFactura?: boolean } = {},
): number {
  let total = 0;
  for (const c of rows) {
    const status = c.fields.Status_P ?? '';
    if (status === 'Anulado') {
      // Excepción: un comprobante anulado que YA tiene factura emitida sigue
      // ocupando techo de facturación. Anular es un soft-delete de una sola
      // columna (`Status_P='Anulado'`) — no toca `cae` ni `nro_comprobante`,
      // así que el comprobante sigue vivo ante ARCA. Si lo descontáramos,
      // `disponibleAFacturar` volvería al techo completo y se podría facturar
      // la misma plata dos veces, con dos CAE reales y un solo pago. Sólo lo
      // libera la Nota de Crédito: una NC total limpia `facturado` en el
      // padre y ahí la fila deja de contar sola.
      //
      // ponytail: una NC PARCIAL no limpia el padre, así que la fila sigue
      // contando entera — sobreestima el techo por lo acreditado. Conservador
      // a propósito: nunca habilita facturar de más. Si molesta, hay que netear
      // contra `nota_credito_de_id` acá.
      if (!(opts.requireFactura && c.fields.Facturado_CP === 'SI')) continue;
    }
    // A promesa de pago (Pendiente de Cobro) is a scheduled future
    // collection, not money in. Callers measuring actual cobranza pass
    // excludePromesas so it counts as pendiente, not pagado.
    if (opts.excludePromesas && status === 'Pendiente de Cobro') continue;
    if (opts.requireFactura && c.fields.ContieneFactura_CP !== 'SI') continue;
    // Sin factura = ni marcado ni facturado: una fila vieja con factura
    // emitida y el flag vacío no tiene que comerse el cupo sin factura.
    if (
      opts.sinFactura &&
      (c.fields.ContieneFactura_CP === 'SI' || c.fields.Facturado_CP === 'SI')
    ) {
      continue;
    }
    const medio = c.fields.MedioPago_CP ?? '';
    const isUsdMedio =
      cajaBase(medio) === 'Efectivo Dolar' ||
      medio === 'Transferencia Galicia Dolar' ||
      medio === 'Transferencia Dolar';
    // Pesos_CP holds the converted amount when the pago currency ≠ venta
    // currency; fall back to Monto_CP otherwise.
    const raw =
      (moneda === 'ARS' && isUsdMedio) || (moneda === 'USD' && !isUsdMedio)
        ? parseMoney(c.fields.Pesos_CP) ?? parseMoney(c.fields.Monto_CP) ?? 0
        : parseMoney(c.fields.Monto_CP) ?? 0;
    total += raw;
  }
  return round2(total);
}

function buildSaveMediosPayload(args: {
  moneda: Moneda;
  breakdown: InvoiceBreakdown;
  porcentaje: number;
  medioRows: MedioRow[];
  pagosActivos: Array<{ id: string; fields: { MedioPago_P?: string | null } }>;
  homeCurrency: 'ARS' | 'MXN';
}): SaveMediosPagoPayload {
  const { moneda, breakdown, porcentaje, medioRows, pagosActivos, homeCurrency } = args;

  // Rows currently checked (included in the save).
  const checkedRows = medioRows.filter((r) => r.pagoUnico || r.distPagos);

  // Existing Pagos whose medio isn't checked anymore → send to anular.
  const checkedMedios = new Set(checkedRows.map((r) => r.medio));
  const anularPagoIds = pagosActivos
    .filter((p) => !checkedMedios.has((p.fields.MedioPago_P ?? '').trim()))
    .map((p) => p.id);

  const medios = checkedRows.map((r) => {
    const medioMoneda = r.categoria === 'Internacional' ? 'USD' : homeCurrency;
    const monto = parseMoney(r.monto) ?? 0;
    const tipoCambio = parseMoney(r.tipoCambio) ?? undefined;
    let montoDolar: number | undefined;
    if (medioMoneda !== moneda && tipoCambio && tipoCambio > 0) {
      // venta in local (ARS/MXN) + medio in USD: persisted "dólar" amount
      // is monto × TC (i.e., local equivalent). The field name is legacy
      // (Pesos_CP / MontoDolar_P) but it stores the converted amount.
      montoDolar =
        moneda === 'ARS' || moneda === 'MXN' ? monto * tipoCambio : monto / tipoCambio;
    }
    return {
      pagoId: r.pagoId,
      medio: r.medio,
      categoria: r.categoria,
      pagoUnico: r.pagoUnico,
      distPagos: r.distPagos,
      monto,
      tipoCambio: tipoCambio && tipoCambio > 0 ? tipoCambio : undefined,
      montoDolar,
      cuotas: r.admiteCuotas && r.cuotas ? r.cuotas : undefined,
    };
  });
  const recargo = recargoDeFilas(medioRows);

  return {
    moneda,
    breakdown: {
      porcentajeFactura: porcentaje,
      iva21: round2(breakdown.subtotalIva21 * 0.21),
      iva105: round2(breakdown.subtotalIva105 * 0.105),
      montoFactura: round2(breakdown.montoFactura),
      ivaACobrar: round2(breakdown.ivaACobrar),
      totalFactura: round2(breakdown.totalFactura),
      // Persisted MontoCobroEfectivo_RO mirrors the auto-computed
      // un-invoiced portion, not the user's tally of cash medios.
      montoCobroEfectivo: round2(breakdown.montoCobroEfectivo),
      montoTotalCobro: round2(breakdown.montoTotalCobro),
      recargoMp:
        recargo && breakdown.recargoMp > 0
          ? { cuotas: recargo.cuotas, pct: recargo.pct, monto: round2(breakdown.recargoMp) }
          : null,
    },
    medios,
    anularPagoIds,
  };
}

/**
 * Compact dot separator for the toolbar metadata strip. Returns null
 * when `show` is false so callers can drop the separator before/after
 * an absent field without an extra wrapping ternary at the call site.
 */
function Sep({ show = true }: { show?: boolean }) {
  if (!show) return null;
  return <span className="text-muted-foreground/40">·</span>;
}
